/* ============================================================================
   claude-3d.js — claude-3d-hero (Three.js carousel) + claude-underbill-3d
   (GSAP flip cards). TALOS, 2026-08-16. DRAFT THEME ONLY.

   Design constraints, in order:
   1. ~90% of traffic is a phone. DPR capped, unlit quads only, render loop
      pauses off-screen and on hidden tabs. The product photos are already lit;
      re-lighting them in WebGL would cost frames and muddy them.
   2. Never break shopping. Everything renders server-side first; this file only
      ENHANCES. No WebGL / reduced-motion / CDN failure => the static hero image
      stays, thumbs still switch products, every link still works.
   3. Vertical scroll belongs to the page (touch-action: pan-y on the stage).
      The carousel listens to horizontal drag only.

   Libraries are VENDORED INTO THE THEME (claude-vendor-three.js, r158 UMD;
   claude-vendor-gsap.js + claude-vendor-scrolltrigger.js, 3.12.5) and loaded
   lazily as CLASSIC scripts. This is deliberate and load-bearing: the first
   build imported them from jsDelivr as cross-origin ES modules, which worked
   on the CLI dev server and was blocked in Shopify's theme-editor/preview
   context — the 3D silently never booted for Adam (2026-08-16). Classic
   same-CDN <script> tags have no CORS/CSP surface to trip. Do not put the
   import() back. The sections publish the asset URLs on window.__c3dVendor
   before this file runs.
   The theme editor destroys/re-creates section DOM, so init is idempotent and
   re-run on shopify:section:load.
   ============================================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var scriptWaits = {};
  function loadScript(src) {
    if (!scriptWaits[src]) {
      scriptWaits[src] = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('failed: ' + src)); };
        document.head.appendChild(s);
      });
    }
    return scriptWaits[src];
  }

  function loadLibs(wantThree) {
    var urls = window.__c3dVendor || {};
    var chain = loadScript(urls.gsap).then(function () { return loadScript(urls.st); });
    if (wantThree) chain = chain.then(function () { return loadScript(urls.three); });
    return chain.then(function () {
      if (!window.gsap || !window.ScrollTrigger || (wantThree && !window.THREE)) {
        throw new Error('vendor globals missing');
      }
      window.gsap.registerPlugin(window.ScrollTrigger);
      return { gsap: window.gsap, ScrollTrigger: window.ScrollTrigger, THREE: wantThree ? window.THREE : null };
    });
  }

  /* ------------------------------------------------------------------ HERO */
  function initHero(root) {
    /* guard is PER-ENGINE: the collection page mounts the hero AND the
       glass on one root, and a shared flag let whichever ran first
       lock the other out (2026-08-17) */
    if (root.dataset.c3dHeroInit) return;
    root.dataset.c3dHeroInit = '1';

    var dataEl = root.querySelector('[data-c3d-products]');
    if (!dataEl) return;
    var products;
    try { products = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!products.length) return;

    var stage = root.querySelector('.c3d-stage');
    if (!stage) return;   /* no ring on this page (mixed collections, 2026-09-25): nothing to build, no 3D library */
    var hint = root.querySelector('.c3d-stage__hint');
    var elTitle = root.querySelector('[data-c3d-title]');
    /* the collection page keeps its copy STILL — no title ticker, no
       swatch sway (Adam, 2026-08-17: "the swatches and flatbill don't
       need to be moving left to right with the hats"); the homepage
       keeps both on purpose */
    var staticCopy = root.hasAttribute('data-c3d-static-copy');
    var elSub = root.querySelector('[data-c3d-sub]');
    var elPrice = root.querySelector('[data-c3d-price]');
    var elStock = root.querySelector('[data-c3d-stock]');
    var elCta = root.querySelector('[data-c3d-cta]');
    /* the collection-page hero carries ADD TO CART under the swatch row
       (Adam, 2026-08-17) — follows the focused hat like the CTA does */
    var elAtc = root.querySelector('[data-c3d-atc]');
    if (elAtc) elAtc.addEventListener('click', function () {
      var vid = elAtc.getAttribute('data-variant');
      if (!vid || elAtc.disabled) return;
      elAtc.disabled = true;
      elAtc.textContent = 'Adding…';
      var add = window.__c3dCartAdd || function (id) {
        return new Promise(function (res, rej) {
          var x = new XMLHttpRequest();
          x.open('POST', '/cart/add.js');
          x.setRequestHeader('Content-Type', 'application/json');
          x.onload = function () { if (x.status >= 200 && x.status < 300) res(); else rej(new Error('cart ' + x.status)); };
          x.onerror = function () { rej(new Error('network')); };
          x.send(JSON.stringify({ id: Number(id), quantity: 1 }));
        });
      };
      add(Number(vid)).then(function () {
        elAtc.textContent = 'Added ✓';
        elAtc.disabled = false;
      }).catch(function () {
        elAtc.textContent = 'Try again';
        elAtc.disabled = false;
      });
    });
    var fallbackImg = root.querySelector('.c3d-stage__fallback img');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('.c3d-thumb'));
    var active = 0;

    /* the tap hint only shows where there is an underbill story to find:
       hidden for products with no underbill image and for the categories Adam
       named (paddles, ropes, boonies, beanies, stickers), and hidden for good
       once a flip has actually been performed */
    var tapHintEl = root.querySelector('.c3d-taphint');
    var tapHintDone = false;
    /* the no-underbill families (Adam, 2026-08-28: ropes, beanies,
       boonies, stickers, pickleball paddles, towels — none flip) */
    var tapHintExcl = /BOONIE|BEANIE|ROPE|PADDLE|STICKER|TOWEL|BLANK/i;
    function updateTapHint(p) {
      /* label AND title: the rope labels became colors (2026-08-28), so
         the family words only reliably live in the title now */
      var ubOk = !!p.ub && !tapHintExcl.test((p.label || '') + ' ' + (p.title || ''));
      /* the collection pages' stage hint reads "Click to view underbill"
         (CSS copy swap) — on a product with nothing to flip (ropes,
         beanies, paddles…) that's a lie, so it hides too (Adam's rope
         page, 2026-08-28). Hide-only: a drag already retires it. */
      if (hint && !ubOk && root.closest('.cc-claude-collection-3d')) {
        hint.classList.add('is-hidden');
      }
      if (!tapHintEl) return;
      var show = !tapHintDone && ubOk;
      tapHintEl.classList.toggle('is-hidden', !show);
    }

    function paintCopy(i, gsap) {
      var p = products[i];
      updateTapHint(p);
      /* sub/price/stock hooks were removed from the hero markup 2026-08-16
         (collections showcase) — every hook is optional from here on.
         The TITLE is deliberately not in the animated set: in the WebGL path
         the render loop drives it per-frame so it swipes WITH the hat. */
      var swapEls = [elSub, elPrice, elStock].filter(Boolean);
      var swap = function () {
        if (elTitle) elTitle.textContent = p.label;
        if (elSub) elSub.textContent = p.title;
        if (elPrice) elPrice.textContent = p.price;
        if (elStock) elStock.textContent = p.available ? p.stockText : p.soldOutText;
        if (elCta) {
          /* ctaText = fixed label ("View Product" on collection pages, Adam
             2026-08-20); without it, prefix + style name ("Shop USA"). */
          elCta.textContent = p.ctaText || (p.ctaPrefix + ' ' + p.label);
          elCta.setAttribute('href', p.url);
        }
        if (elAtc) {
          elAtc.setAttribute('data-variant', p.variant || '');
          elAtc.disabled = !p.available || !p.variant;
          elAtc.textContent = p.available && p.variant ? 'Add to cart' : p.soldOutText;
          /* claude- (2026-08-28, Adam): the HERO was the one glass surface
             still showing a dead Sold out button — swap it for the same
             email → Klaviyo back-in-stock block the grid cards and overlay
             use. Per-product: it appears only while the active hat is sold
             out and hands the slot back the moment a swipe lands on stock.
             __c3dNotifyControl boots with the glass engine, so late-bind. */
          var oldNotify = elAtc.parentNode && elAtc.parentNode.querySelector('.c3d-ovl__notify');
          if (oldNotify) oldNotify.remove();
          var wantNotify = elAtc.disabled && p.variant &&
            typeof window.__c3dNotifyControl === 'function';
          var heroCtl = wantNotify ? window.__c3dNotifyControl(Number(p.variant)) : null;
          if (heroCtl) {
            heroCtl.classList.add('c3d-colhero__notify');
            elAtc.style.display = 'none';
            elAtc.parentNode.insertBefore(heroCtl, elAtc);
          } else {
            elAtc.style.display = '';
            /* first paint can beat the glass engine's boot — repaint
               shortly, but only while this hat is still the active one */
            if (elAtc.disabled && p.variant && typeof window.__c3dNotifyControl !== 'function' &&
                (window.__c3dHeroNotifyTries = (window.__c3dHeroNotifyTries || 0) + 1) < 15) {
              setTimeout(function () { if (active === i) paintCopy(i); }, 700);
            }
          }
        }
        if (fallbackImg) { fallbackImg.src = p.img; fallbackImg.alt = p.alt; }
      };
      thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      /* static strip: glide the active swatch into view instead of
         sliding the whole row */
      if (staticCopy && thumbs[i] && thumbs[i].parentNode) {
        var strip = thumbs[i].parentNode;
        strip.scrollTo({
          left: thumbs[i].offsetLeft - strip.clientWidth / 2 + thumbs[i].offsetWidth / 2,
          behavior: 'smooth'
        });
      }
      if (gsap && !reduceMotion && swapEls.length) {
        /* micro animation: copy block dips and returns as the product swaps */
        gsap.timeline()
          .to(swapEls, { y: 8, autoAlpha: 0, duration: .16, stagger: .02, ease: 'power1.in', onComplete: swap })
          .to(swapEls, { y: 0, autoAlpha: 1, duration: .3, stagger: .04, ease: 'power2.out' });
      } else {
        swap();
      }
    }

    /* hero navigation goes through the GLASS when the glass engine is on
       the page (Adam, 2026-08-17: "when i click on the swatches in the
       hero it doesn't open glass") — real navigation only as fallback */
    function heroNav(url) {
      if (window.__c3dGlassOpenCollection && window.__c3dGlassOpenCollection(url)) return;
      window.location.href = url;
    }

    function goStatic() {
      root.classList.remove('c3d--live');
      root.classList.add('c3d--static');
      if (hint) hint.classList.add('is-hidden');
      var sw = root.querySelector('.c3d-swipe');
      if (sw) sw.classList.add('is-hidden');   /* nothing to swipe without 3D */
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var k = +t.dataset.i;
          if (k === active) { heroNav(products[k].url); return; }
          active = k; showingUb = false; paintCopy(active, null);
        });
      });
      /* static fallback mirrors the live ring's tap contract: a tap
         enters the collection/product (Adam, 2026-08-28) — hero and
         collection rings alike. */
      stage.addEventListener('click', function () {
        var p = products[active];
        if (p) heroNav(p.url);
      });
    }

    /* scroll choreography runs with or without WebGL — motion permitting */
    if (!reduceMotion) loadLibs(false).then(initScrollFX).catch(function () {});

    if (reduceMotion || !window.WebGLRenderingContext) { goStatic(); return; }

    loadLibs(true).then(function (libs) {
      var THREE = libs.THREE, gsap = libs.gsap;
      var renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
        /* the canvas turns WHITE in the frame the browser holds during
           navigation (Adam, 2026-08-28) — preserveDrawingBuffer made it
           worse, so instead the stage BLANKS the moment a real navigation
           starts (delegated click below) and the dark water shows through. */
      } catch (e) { return goStatic(); }
      if (!renderer.getContext()) return goStatic();

      root.classList.add('c3d--live');
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, window.innerWidth < 769 ? 1.75 : 2));
      renderer.setSize(stage.clientWidth, stage.clientHeight);
      stage.appendChild(renderer.domElement);

      /* OLDER-IPAD RESCUE (Adam, 2026-08-28: Parker's dad's older iPad
         "has many issues"): memory-starved WebKit kills WebGL contexts
         mid-session — without this handler the hero freezes into a dead
         canvas. We already fall back when the context fails to START
         (goStatic above); this demotes the same way when it dies LATER.
         No restore attempt: on a device shedding GPU memory, the static
         hero is the right place to stay. */
      var contextDead = false;
      renderer.domElement.addEventListener('webglcontextlost', function (ev) {
        ev.preventDefault();
        contextDead = true;              /* tick() checks this and stops */
        try { renderer.dispose(); } catch (err) {}
        if (renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
        goStatic();
      });

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(34, stage.clientWidth / stage.clientHeight, 0.1, 100);
      camera.position.set(0, 0.10, 5.0);

      function fitCamera() {
        camera.aspect = stage.clientWidth / stage.clientHeight;
        camera.updateProjectionMatrix();
      }
      fitCamera();

      var group = new THREE.Group();
      scene.add(group);

      /* soft contact shadow, drawn once */
      var shadowTex = (function () {
        var c = document.createElement('canvas'); c.width = c.height = 128;
        var ctx = c.getContext('2d');
        var g = ctx.createRadialGradient(64, 64, 2, 64, 64, 62);
        g.addColorStop(0, 'rgba(0,0,0,.55)'); g.addColorStop(.55, 'rgba(0,0,0,.18)'); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
        return new THREE.CanvasTexture(c);
      })();

      var loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');
      var H = 2.5;
      var cards = [];

      /* (the focus glow lived here for one round on 2026-08-16 — Adam had it
         removed the same day; do not re-add without a fresh ask) */

      products.forEach(function (p) {
        var holder = new THREE.Group();
        var shadow = new THREE.Mesh(
          new THREE.PlaneGeometry(2.6, 0.8),
          new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false, opacity: .9 })
        );
        shadow.position.set(0, -1.06, -0.02);
        /* the shadow keeps its HOMEPAGE footprint no matter how far the
           hat itself is scaled up — the collection page runs the hats at
           1.22 and the scaled shadows widened into one smear ("they
           merge into each other", 2026-08-17) */
        var shInv = 1 / (p.scale || 1);
        shadow.scale.set(shInv, shInv, 1);
        holder.add(shadow);

        /* DoubleSide: the back of the ring faces away — single-sided planes
           would cull to nothing exactly where the faint circle should show */
        var mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, opacity: 0, side: THREE.DoubleSide });
        var mesh = new THREE.Mesh(new THREE.PlaneGeometry(H, H), mat);
        holder.add(mesh);

        var card = { holder: holder, mesh: mesh, mat: mat, shadowMat: shadow.material,
                     frontTex: null, ubTex: null, flipped: false, flipping: false,
                     scaleMul: p.scale || 1, dy: (p.dy || 0) * H };
        /* PERF (Adam, 2026-08-16: "make sure we are optimizing everything"):
           textures load STAGGERED — the visible neighbourhood immediately,
           the rest when the browser is idle — so the hero never front-loads
           nine product images on a phone connection. */
        card.loadTex = function () {
          if (card.texRequested) return;
          card.texRequested = true;
          loader.load(p.img, function (tex) {
            tex.colorSpace = THREE.SRGBColorSpace;
            tex.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
            var ar = tex.image.width / tex.image.height;
            mesh.geometry.dispose();
            mesh.geometry = new THREE.PlaneGeometry(H * ar, H);
            mat.map = tex; mat.needsUpdate = true;
            card.frontTex = tex;
            card.texReady = true;   /* the fade-in starts from here — see layout() */
          });
        };

        group.add(holder);
        cards.push(card);
      });
      /* front three now; everyone else on idle — on the HOMEPAGE hero.
         The COLLECTION ring (P-1154, Adam 2026-09-25: "like 10 total photos
         load ... as the swipe happens the next loads") keeps a window of
         ten around the front hat instead: see loadWindow below. */
      var windowed = !!root.closest('.cc-claude-collection-3d');
      function loadWindow(center) {
        var Nc = cards.length;
        for (var w = -5; w <= 4; w++) {
          var k = (((center + w) % Nc) + Nc) % Nc;
          if (cards[k]) cards[k].loadTex();
        }
      }
      if (!windowed) {
        [0, 1, cards.length - 1].forEach(function (i) { if (cards[i]) cards[i].loadTex(); });
        (window.requestIdleCallback || function (f) { setTimeout(f, 2500); })(function () {
          cards.forEach(function (c) { c.loadTex(); });
        }, { timeout: 5000 });
      }

      /* ---- tap the focused hat -> flip to its solo underbill (Adam,
         2026-08-16). Classic card-flip: rotate to edge-on, swap the texture,
         jump to the mirrored edge and finish — so the underbill photo is never
         shown mirror-imaged. Drag vs tap is decided by pointer travel. ---- */
      function texFor(card, url, cb) {
        if (card.ubTex) return cb(card.ubTex);
        loader.load(url, function (tex) {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
          card.ubTex = tex; cb(tex);
        });
      }
      function unflip(i) {
        var c = cards[i];
        if (!c || !c.flipped || !c.frontTex) return;
        c.mesh.rotation.y = 0; c.mat.map = c.frontTex; c.mat.needsUpdate = true;
        c.flipped = false;
      }
      function flipActive() {
        var p = products[norm(active)], c = cards[norm(active)];
        if (!p.ub || !c || c.flipping || !c.frontTex) return;
        if (tapHintExcl.test(p.label || '')) return;   /* no-underbill categories never flip */
        c.flipping = true;
        /* the tap hint's job is done the first time a flip happens; swipes
           deliberately never dismiss it (Adam, 2026-08-16) */
        tapHintDone = true;
        if (tapHintEl) tapHintEl.classList.add('is-hidden');
        var toUb = !c.flipped;
        var apply = function (tex) {
          gsap.to(c.mesh.rotation, {
            y: Math.PI / 2, duration: .22, ease: 'power2.in',
            onComplete: function () {
              c.mat.map = tex; c.mat.needsUpdate = true;
              c.mesh.rotation.y = -Math.PI / 2;
              gsap.to(c.mesh.rotation, {
                y: 0, duration: .34, ease: 'back.out(1.6)',
                onComplete: function () { c.flipped = toUb; c.flipping = false; }
              });
            }
          });
        };
        if (toUb) texFor(c, p.ub, apply); else apply(c.frontTex);
      }

      /* CLOSED RING (Adam, 2026-08-16): the hats occupy a full circle —
         STEP is exactly 2π/N, so the N cards close the loop and the far side
         of the ring floats VAGUELY VISIBLE behind the focused hat ("floating
         in circle in space"). Wrapping is free: `pos`/`target`/`active` run
         unbounded, norm() maps to a product index, sdelta() gives the
         shortest signed distance around the ring. */
      var N = products.length;
      /* the LOOK never changes with the hat count (Adam, 2026-08-17:
         "i didn't want it to work different just have all the hats" —
         18 freedom hats first shrank the angle, then a bigger radius
         flattened the depth). Geometry is pinned to the homepage's
         12-slot ring for every N: since only the front three render,
         the ring never needs to close visually — wrapping stays with
         norm()/sdelta() over the real N. */
      var STEP = (Math.PI * 2) / 12, R = 3.4;
      /* per-section ring tuning (collection page, 2026-08-17): its short
         wide stage pulls neighbours inward and stretches their turned
         faces — spread pushes them back out, skew eases the rotation.
         Absent attributes = 1 = the homepage exactly as it was. */
      var spreadX = parseFloat(root.getAttribute('data-c3d-spread') || '1') || 1;
      var skewMul = parseFloat(root.getAttribute('data-c3d-skew') || '1') || 1;
      /* claude- (2026-08-28, Adam: "the stretch of the bill on the left hat
         ... is super annoying"): the neighbours' Y-rotation is what warps a
         flat hat photo under perspective — the near edge of the turned plane
         balloons. A section can carry a DESKTOP-ONLY skew so the big-screen
         ring holds its hats nearly flat while mobile keeps its look. */
      var skewDesk = parseFloat(root.getAttribute('data-c3d-skew-desk') || '');
      /* desktop-only side-hat opacity (Adam, 2026-08-28: "left and right
         show be fading out a bit") — the immediate neighbours' opacity at
         one card out; absent = the shared 0.95 near-solid look. */
      var sideFadeDesk = parseFloat(root.getAttribute('data-c3d-sidefade-desk') || '');
      /* desktop-only world-Y lift (Adam, 2026-08-28: "the shadow is barely
         getting cut off") — the contact shadow hangs 1.06 under each hat
         and the collection stage's bottom edge was shaving it. Raising the
         hats INSIDE the canvas is the only fix that moves the shadow too. */
      var dyDesk = parseFloat(root.getAttribute('data-c3d-dy-desk') || '') || 0;
      /* desktop-only scale compensator (Adam, 2026-08-28): hat pixel size
         rides the canvas HEIGHT, so shrinking the stage's dead air would
         shrink the hats with it — this multiplier holds their size while
         the frame tightens around them. */
      var scaleDesk = parseFloat(root.getAttribute('data-c3d-scale-desk') || '') || 1;
      /* desktop-only neighbour spread (Adam, 2026-08-28: "a little too
         spread apart ... bring them both into center just a little") */
      var spreadDesk = parseFloat(root.getAttribute('data-c3d-spread-desk') || '');
      /* FIVE-UP ring, desktop only (Adam, 2026-08-28, hat ropes): when
         set, ±2 neighbours render too, at this opacity — a shade fainter
         than the pair beside the centre. */
      var vis2Desk = parseFloat(root.getAttribute('data-c3d-vis2-desk') || '');
      /* collection pages LAND ATTACHED (Adam, 2026-08-20): no glide-in, no
         teaching sway — but the idle float stays ("i still wanted them to
         float"). The attribute name outlived its first meaning. */
      var noFloat = root.hasAttribute('data-c3d-nofloat');
      function norm(i) { return ((i % N) + N) % N; }
      function sdelta(d) { return ((((d + N / 2) % N) + N) % N) - N / 2; }
      /* the 1.25 desktop right-shift existed for the OLD full-width hero
         where the copy overlaid the stage's left. The 2026-08-27 two-column
         layout gives the hats their own right column, so the ring centres
         in it (shift retired; leave the plumbing for rollback). */
      var xShift = 0;
      var deskMQ = window.matchMedia('(min-width: 1024px)');
      function updShift() { xShift = 0; }
      updShift();
      /* start off-mark and settle: the settle IS the drag affordance.
         EXCEPT collection pages (Adam, 2026-08-20: "the 3d hats i want to
         land right there attached when opening the page") — they open
         already in position, no glide, no sway. */
      /* claude- (2026-08-27, Adam: "that section should be centered under
         the hats"): DESKTOP opens already centered — the off-mark settle
         read as misalignment on a big screen. Mobile keeps its glide. */
      var openCentered = noFloat || deskMQ.matches;
      /* claude- (2026-08-28, Adam): "the hat showing first on the carousel
         should never be a sold out hat" — land on the first IN-STOCK hat.
         The ring order is untouched (top sellers keep their slots, sold-out
         hats still ride the ring); we only choose where to open. If the
         whole line is sold out, index 0 stands. */
      for (var si0 = 0; si0 < N; si0++) {
        if (products[si0].available) { active = si0; break; }
      }
      if (windowed) loadWindow(active);   /* ten around the opening hat */
      var pos = openCentered ? active : active - 0.75, target = active;
      var spin = openCentered ? 0 : -0.28, spinTarget = 0;

      /* the teaching rock (mobile, from load until the first real swipe):
         the ring sways gently so the hats advertise their own swipe-ability
         while the static universal-swipe glyph sits below them (Adam,
         2026-08-16, third design — the steps and the gliding finger are
         gone). The offset is applied at DRAW time only — pos and target
         never see it, so taps, dots and releases stay exact. */
      var demoOn = !reduceMotion && !deskMQ.matches && !noFloat;
      var demoAmp = 0;

      /* the title rides the drag (Adam, 2026-08-16: "the title swipes with
         the hat"): every frame it slides and fades with the carousel's
         fractional position, and its text flips the moment the nearest card
         changes — a ticker attached to the front hat, not a caption. */
      var lastNearest = -1;
      function followTitle(p) {
        if (!elTitle) return;
        var nearest = Math.round(p);                /* unbounded — wraps via norm */
        var frac = p - nearest;
        if (norm(nearest) !== lastNearest) {
          lastNearest = norm(nearest);
          elTitle.textContent = products[lastNearest].label;
        }
        if (staticCopy) return;
        elTitle.style.transform = 'translateX(' + (-frac * 130).toFixed(1) + 'px)';
        elTitle.style.opacity = Math.max(0, 1 - Math.abs(frac) * 1.8).toFixed(3);
      }

      function layout(dt, t) {
        /* two ease rates, same lerp (Adam, 2026-09-02: the phone swipe was
           "still too slow" at every gearing — the lag was the ring easing
           toward the finger at the coast rate). Finger down: chase at 14 so
           the hats feel attached; after release: 6, the glass glide coast.
           Same split the rails use in railTo. */
        pos += (target - pos) * Math.min(1, dt * (dragging ? 14 : 6));
        spin += (spinTarget - spin) * Math.min(1, dt * 5);
        /* the teaching rock: eased in on load, eased out the moment a real
           finger lands or the demo retires — never a snap. Finger right =
           hats right, matching real drag physics (drag right lowers pos). */
        demoAmp += ((demoOn && !dragging ? 1 : 0) - demoAmp) * Math.min(1, dt * 2.5);
        var demoOff = 0;
        if (demoAmp > 0.002) {
          /* ~8.2s per full sway at barely-there travel — slowed twice at
             Adam's call ("calming", then "slow down its distracting").
             This is now the ONLY swipe cue: a breath, not a demo. */
          demoOff = -Math.sin(t / 1300) * 0.075 * demoAmp;
        }
        followTitle(pos + demoOff);
        var pm = (((pos + demoOff) % N) + N) % N;
        cards.forEach(function (c, i) {
          var nd = sdelta(i - pm);                /* signed distance in cards */
          var a = nd * STEP;                      /* full circle: a ∈ (-π, π] */
          var depth = (Math.cos(a) + 1) / 2;      /* 1 = front, 0 = back of ring */
          var spr = (deskMQ.matches && !isNaN(spreadDesk)) ? spreadDesk : spreadX;
          c.holder.position.x = Math.sin(a) * R * spr + xShift;
          c.holder.position.z = Math.cos(a) * R - R;
          c.holder.position.y = Math.sin(t / 1400 + i) * 0.045 + c.dy +
            (deskMQ.matches ? dyDesk : 0);  /* idle float + per-hat offset — stays everywhere (Adam, 2026-08-20: "i still wanted them to float") */
          var sk = (deskMQ.matches && !isNaN(skewDesk)) ? skewDesk : skewMul;
          c.holder.rotation.y = -Math.sin(a) * 0.75 * sk + spin * 0.6;
          var s = (0.38 + 0.62 * depth) * c.scaleMul * (deskMQ.matches ? scaleDesk : 1);
          c.holder.scale.setScalar(s);
          /* ONLY THE FRONT THREE show (Adam, 2026-08-16, superseding the
             vaguely-visible full circle the same day): centre + immediate
             left/right at strong opacity, everything past them fades to
             nothing by 1.6 cards out — the ring still SCROLLS circularly,
             it just doesn't render its back side. */
          var adx = Math.abs(nd);
          /* neighbours: MOBILE at 0.75 (Adam, 2026-08-28: "fade out the 2
             hats on the left and right on mobile a little bit"); DESKTOP
             stays near-solid 0.95 unless its attr says otherwise (Adam,
             same day: "don't make the hats fade out on desktop" — the
             first cut let the mobile default leak up). */
          var nf = deskMQ.matches
            ? (isNaN(sideFadeDesk) ? 0.95 : sideFadeDesk)
            : 0.75;
          var vis;
          if (deskMQ.matches && !isNaN(vis2Desk)) {
            /* five in view: centre 1, ±1 at nf, ±2 at vis2Desk, gone by 2.6 */
            if (adx <= 1) vis = 1 - (1 - nf) * adx;
            else if (adx <= 2) vis = vis2Desk + (nf - vis2Desk) * (2 - adx);
            else vis = Math.max(0, vis2Desk * (2.6 - adx) / 0.6);
          } else {
            vis = adx <= 1 ? (1 - (1 - nf) * adx) : Math.max(0, nf * (1.6 - adx) / 0.6);
          }
          /* an untextured MeshBasicMaterial renders as a SOLID WHITE plane —
             the "white boxes" flashing on every page change (Adam,
             2026-08-25). Nothing shows until its texture is decoded; then a
             quick fade-in instead of a pop. */
          if (!c.texReady) {
            c.fadeIn = 0;
            c.mat.opacity = 0;
            c.shadowMat.opacity = 0;
            c.holder.visible = false;
          } else {
            c.fadeIn = Math.min(1, (c.fadeIn || 0) + dt * 3.2);
            c.mat.opacity = vis * c.fadeIn;
            c.shadowMat.opacity = vis * 0.9 * depth * c.fadeIn;
            c.holder.visible = vis > 0.02;
          }
          c.holder.renderOrder = Math.round(2 + 100 * depth);
        });
        group.rotation.y = spin * 0.15;

        /* the swatch STRIP rides the SAME pos as the hats (Adam: "as you
           swipe on the hat the swatches transition with it") — a FLAT row,
           not orbital (Adam, 2026-08-16): dots slide linearly, fading in
           from the left edge and out to the right, with a slight size lift
           on whichever dot holds the centre. Still wraps via sdelta. */
        var halfW = (rail ? rail.clientWidth : stage.clientWidth) / 2;
        if (staticCopy) return;   /* plain scrollable row, no sway */
        for (var j = 0; j < thumbs.length; j++) {
          var dd = sdelta(j - pm);
          var x = dd * 52;
          var th = thumbs[j];
          th.style.transform = 'translateX(' + x.toFixed(1) + 'px)'
            + ' scale(' + (1 + 0.16 * Math.max(0, 1 - Math.abs(dd))).toFixed(3) + ')';
          th.style.opacity = Math.max(0, Math.min(1, 1.15 - Math.abs(x) / (halfW * 0.92))).toFixed(3);
          th.style.zIndex = String(50 - Math.round(Math.abs(dd)));
        }
      }

      /* (the ambient-show system — fireworks over USA, and before that a
         five-effect suite — lived here on 2026-08-16 and was removed the same
         day at Adam's call, favouring load-time discipline. History in the
         changelog; re-add only on a fresh ask.) */

      var dragging = false, startX = 0, startPos = 0, moved = 0;
      /* GLASS GLIDE on the hero ring (Adam, 2026-08-28: "the hero hats
         when i swipe in the hero hats give it the glass glide") — the
         flick's velocity throws `target` further before it rounds, and
         the render loop's own lerp (dt*6) streams the hats past and eases
         in. The ring still finishes ON a hat: title/copy/swatches follow
         the front hat, so resting between two is not an option here. */
      var velX = 0, lastVX = 0, lastVT = 0;
      function velTrack(e) {
        var vdt = e.timeStamp - lastVT;
        if (vdt > 0) velX = Math.max(-4, Math.min(4, (e.clientX - lastVX) / vdt));
        lastVX = e.clientX; lastVT = e.timeStamp;
      }
      /* THE SWATCH RULES, ON THE STAGE (Adam, 2026-08-28: "i love the way
         that they swipe and the hero isn't the same") — the hero used to
         gear its drag to the stage width (~157px of finger per hat on a
         phone, ~540 on desktop) while the swatch ring runs a fixed 26px
         per dot with a 1-3 dot flick bonus. Same ring, same feel now. */
      var STAGE_PX_PER_HAT = 63;   /* walked 26 -> 35 -> 47 -> 63 on 2026-08-28 ("still too fast"); 2026-09-02 tried 47 and 35 in the pane, "way too fast" on a real phone once the finger-tracking lerp landed, so back to 63 ("the swiping on the mobile carousel too slow now") — phone only; desktop is STAGE_PX_PER_HAT_DESK */
      /* DESKTOP WHEEL / TRACKPAD gearing (Adam, 2026-09-02: "on desktop the
         scroll through the hats on carousel needs to be much slower") —
         was 100px of wheel per hat (deltaX * 0.01), now 400. A single
         event is capped so a trackpad momentum burst cannot leap the ring. */
      var WHEEL_PX_PER_HAT = 400;
      /* DESKTOP CLICK-AND-DRAG (Adam, 2026-09-02: "the click and drag is
         still quick on desktop") — a mouse covers 63px in nothing, so on
         desktop the ring gears to 190px of drag per hat (3x) and a flick
         has to be genuinely fast to add anything. Phones keep the glass
         glide numbers untouched. */
      var STAGE_PX_PER_HAT_DESK = 190;
      function stagePx() { return deskMQ.matches ? STAGE_PX_PER_HAT_DESK : STAGE_PX_PER_HAT; }
      function wheelHats(dx) { return Math.max(-120, Math.min(120, dx)) / WHEEL_PX_PER_HAT; }
      function flickExtra() {
        /* knocked down at Adam's call (2026-08-28: "its gotta be the flick
           bonus") — the swatch rail keeps its 1-3 dot throw; the STAGE
           demands a genuinely fast release (0.45 px/ms, was 0.25) and
           tops out at 2 extra hats (was 3, and the gain halved). */
        if (Math.abs(velX) <= (deskMQ.matches ? 0.9 : 0.45)) return 0;   /* desktop mouse: twice the bar (2026-09-02) */
        return Math.min(2, Math.round(Math.abs(velX) * 0.8)) * (velX < 0 ? 1 : -1);
      }
      function W() { return stage.clientWidth; }

      /* a second finger means PINCH, never swipe (Adam, 2026-08-27: "if a
         bill is flipped it flips the bill back") — the pinch's own few px
         of sideways travel read as a real swipe, and the commit path runs
         setActive, which always unflips. Abandon the drag the moment a
         second pointer lands: no commit, no unflip, the zoom is the
         browser's. Shared across the stage and the name row (they share
         `dragging`). */
      var heroPtrs = 0;
      function pinchAbort() {
        dragging = false; spinTarget = 0; target = active;
      }

      /* PINCH TO PEEK (Adam, 2026-08-27: "if i pinch on a slider hat that
         hat just gets released and i can zoom on that hat ... and then i
         let go and it snaps back") — two fingers on the stage zoom the
         whole scene in place, anchored between the fingers, and release
         springs it back to 1. The swipe compass holds the browser's page
         zoom off the stage so these pointer events keep flowing. */
      /* The zoom is the CAMERA, not a CSS scale (Adam, 2026-08-27: "the
         quality of the photo is really bad when zoomed in") — scaling the
         canvas raster blurs at 2x no matter the texture; camera.zoom
         re-renders every frame at native resolution. And the first pinch
         on a card quietly upgrades its 700px texture to 1600px, so the
         cost is paid only by hats that actually get pinched. */
      var pinchPts = new Map(), pinchD0 = 0, pinching = false;
      var pinchAX = 0, pinchAY = 0, pinchM0 = { x: 0, y: 0 }, CAMY = 0.10;
      function pinchDist() {
        var v = Array.from(pinchPts.values());
        return Math.hypot(v[0].x - v[1].x, v[0].y - v[1].y) || 1;
      }
      function pinchHiRes() {
        var card = cards[norm(active)], p = products[norm(active)];
        if (!card || !p || card.hiDone) return;
        card.hiDone = true;
        var hi = (p.img || '').replace(/width=700/, 'width=1600');
        if (hi === p.img) return;
        loader.load(hi, function (tex) {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
          card.frontTex = tex;
          /* only repaint if the front is what's showing — a flipped card
             keeps its underbill until its own flip swaps back */
          if (!card.flipped && !card.flipping) {
            card.mat.map = tex;
            card.mat.needsUpdate = true;
          }
        });
      }
      /* while pinching, the canvas BLEEDS half a scene-height above and
         below and floats over the neighbouring layers (Adam, 2026-08-27:
         "when zoomed in they should be floating above the other layers
         like 3d they are getting cut off below" / "and above") — the hat
         was clipping at the canvas edge, not by CSS. The fov compensation
         keeps the hats exactly the same size while the canvas grows. */
      var pinchBled = false, pinchBaseY = CAMY;
      function pinchBleed(on) {
        if (on === pinchBled) return;
        pinchBled = on;
        var sceneEl = stage.parentElement;
        if (on) {
          /* the TOP bleed reaches the search bar (Adam, 2026-08-27: "the
             top still gets cut off right at the top of $60 area it needs
             to extend at least to the search bar") — measured live, so it
             lands on the header's real edge at any scroll position. The
             hero's own overflow clip yields while the peek is up. */
          var sr = sceneEl.getBoundingClientRect();
          var hdr = document.querySelector('.cc-header');
          var hb = hdr ? hdr.getBoundingClientRect().bottom : 0;
          var bTop = Math.max(Math.round(sceneEl.clientHeight * 0.5), Math.round(sr.top - hb));
          var bBot = Math.round(sceneEl.clientHeight * 0.5);
          stage.style.top = -bTop + 'px';
          stage.style.bottom = -bBot + 'px';
          stage.style.zIndex = '60';
          /* VERTICAL clip only — blanket overflow:visible also released
             the hero's horizontal clip, the page grew ~3x wide behind the
             scenes, and Safari zoomed out to fit it (Adam's squeezed-page
             screenshot, 2026-08-27). clip + visible is the legal combo
             that frees the bleed without creating a scroll container. */
          root.style.overflowX = 'clip';
          root.style.overflowY = 'visible';
          /* asymmetric bleed moves the canvas center — lift the camera so
             the hats hold their exact screen position while the room grows */
          pinchBaseY = CAMY + ((bTop - bBot) / 2) *
            (2 * Math.tan(34 * Math.PI / 360) * 5.0 / sceneEl.clientHeight);
        } else {
          stage.style.top = '';
          stage.style.bottom = '';
          stage.style.zIndex = '';
          root.style.overflowX = '';
          root.style.overflowY = '';
          pinchBaseY = CAMY;
          camera.position.x = 0;
        }
        renderer.setSize(stage.clientWidth, stage.clientHeight);
        camera.fov = 2 * Math.atan(
          Math.tan(34 * Math.PI / 360) * stage.clientHeight / sceneEl.clientHeight
        ) * 180 / Math.PI;
        fitCamera();
        camera.position.y = pinchBaseY;
      }

      /* px/py: how far the two-finger MIDPOINT has travelled — the hats
         follow it (Adam, 2026-08-27: "i also want my two fingers to be
         able to move the hats around the screen so if i am trying to pull
         the hats down i can"). Divided by s so the world tracks the
         fingers 1:1 at any zoom. */
      function pinchApply(s, px, py) {
        camera.zoom = s;
        var wH = 2 * Math.tan(camera.fov * Math.PI / 360) * camera.position.z;
        var wW = wH * camera.aspect;
        camera.position.x = pinchAX * wW * (1 - 1 / s)
          - (px || 0) * (wW / stage.clientWidth) / s;
        camera.position.y = pinchBaseY - pinchAY * wH * (1 - 1 / s)
          + (py || 0) * (wH / stage.clientHeight) / s;
        camera.updateProjectionMatrix();
      }
      /* FAILSAFE (2026-08-27, the squeezed-page bug's second half): when
         Safari takes a gesture it can swallow the pointerups, leaving
         pinchPts stale, heroPtrs high, and the bleed stuck open — the
         next one-finger touch then read as a pinch and the page stayed
         wide. When the LAST finger leaves the screen, the hero's pinch
         state always resolves: stale points cleared, drag counter zeroed,
         and a still-open pinch springs home through the normal path. */
      function pinchRescue(e) {
        if (e.touches.length !== 0) return;
        pinchPts.clear();
        heroPtrs = 0;
        if (pinching) pinchEnd();
      }
      document.addEventListener('touchend', pinchRescue, { passive: true });
      document.addEventListener('touchcancel', pinchRescue, { passive: true });

      function pinchEnd() {
        if (!pinching) return;
        pinching = false;
        var z0 = camera.zoom, x0 = camera.position.x, y0 = camera.position.y;
        var t0 = performance.now();
        (function back(now) {
          var k = Math.min(1, (now - t0) / 280);
          var e = 1 - Math.pow(1 - k, 3);
          camera.zoom = z0 + (1 - z0) * e;
          camera.position.x = x0 * (1 - e);
          camera.position.y = y0 + (pinchBaseY - y0) * e;
          camera.updateProjectionMatrix();
          if (k < 1) requestAnimationFrame(back);
          else pinchBleed(false);   /* canvas returns to its lane after the spring */
        })(t0);
      }

      stage.addEventListener('pointerdown', function (e) {
        pinchPts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        heroPtrs++;
        if (heroPtrs > 1) {
          pinchAbort();
          if (pinchPts.size === 2 && !pinching) {
            pinching = true;
            pinchD0 = pinchDist();
            var v = Array.from(pinchPts.values());
            var r = stage.getBoundingClientRect();
            /* anchor between the fingers, as a fraction from stage center */
            var mx = (v[0].x + v[1].x) / 2, my = (v[0].y + v[1].y) / 2;
            /* grow the canvas first, then measure the anchor against the
               grown rect — the bleed is asymmetric, so the center moves
               and a simple ratio rescale would drift the zoom point */
            pinchBleed(true);
            var r2 = stage.getBoundingClientRect();
            pinchAX = (mx - r2.left) / r2.width - 0.5;
            pinchAY = (my - r2.top) / r2.height - 0.5;
            pinchM0 = { x: mx, y: my };
            pinchHiRes();
          }
          return;
        }
        dragging = true; moved = 0; startX = e.clientX; startPos = target;
        velX = 0; lastVX = e.clientX; lastVT = e.timeStamp;
        stage.setPointerCapture(e.pointerId);
        if (hint) hint.classList.add('is-hidden');
      });
      /* The finger retires on the FIRST sign of a real swipe. Hidden from the
         MOVE handler, not release: on a real phone the browser can steal a
         drag mid-gesture (pointercancel — e.g. a diagonal swipe becoming a
         page scroll) and the release path is then unreliable, which is why
         Adam's device kept the finger after swiping while the dev browser
         didn't. Also retired by any product change (thumbs, keys, wheel). */
      var swipeEl = root.querySelector('.c3d-swipe');
      function retireSwipeHint() {
        demoOn = false;                            /* the rock retires with it */
        if (swipeEl) { swipeEl.classList.add('is-hidden'); swipeEl = null; }
      }

      stage.addEventListener('pointermove', function (e) {
        if (pinchPts.has(e.pointerId)) {
          pinchPts.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pinching && pinchPts.size >= 2) {
            var pv = Array.from(pinchPts.values());
            var pmx = (pv[0].x + pv[1].x) / 2, pmy = (pv[0].y + pv[1].y) / 2;
            pinchApply(Math.max(1, Math.min(3.5, pinchDist() / pinchD0)),
              pmx - pinchM0.x, pmy - pinchM0.y);
            return;
          }
        }
        if (!dragging) return;
        var dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        if (moved >= 8) retireSwipeHint();
        velTrack(e);
        target = startPos - dx / stagePx();
        spinTarget = Math.max(-0.5, Math.min(0.5, -dx / W()));
      });
      function release(cancelled) {
        if (!dragging) return;
        dragging = false; spinTarget = 0;
        /* under 8px of travel is a TAP. On the HOMEPAGE HERO a tap goes
           INTO THE COLLECTION (Adam, 2026-08-28: "a tap should go into
           the collection itself... if you tap the hats, the title or the
           swatch" — supersedes 2026-08-16's tap-always-flips, which made
           sense when the front hid the underbill; the 3-hat composites
           show it up front now). COLLECTION-page rings keep the flip —
           their stage hint still advertises it. A CANCELLED gesture is
           never a tap. */
        if (moved < 8) {
          target = active;
          /* collection rings too (Adam, same day: "do the same for the
             collections pages") — a ring hat there IS a product, so the
             tap opens it; flipActive retired from the tap path. */
          if (!cancelled) heroNav(products[norm(active)].url);
          return;
        }
        /* no clamp — the ring wraps. A flick throws 1-3 extra hats, the
           swatch rail's own rule; a CANCELLED gesture never flings. */
        setActive(Math.round(target) + (cancelled ? 0 : flickExtra()));
      }
      stage.addEventListener('pointerup', function (e) {
        pinchPts.delete(e.pointerId);
        if (pinchPts.size < 2) pinchEnd();
        heroPtrs = Math.max(0, heroPtrs - 1);
        release(false);
      });
      stage.addEventListener('pointercancel', function (e) {
        pinchPts.delete(e.pointerId);
        if (pinchPts.size < 2) pinchEnd();
        heroPtrs = Math.max(0, heroPtrs - 1);
        release(true);
      });
      /* the style-name row swipes too (Adam, 2026-08-28: "i want to be
         able to swipe on all of the carasols") — same drag, minus the
         tap-to-flip (a tap on text should not flip the hat) */
      if (elTitle) {
        elTitle.style.touchAction = 'pan-y pinch-zoom';   /* the swipe compass gates horizontal */
        elTitle.addEventListener('pointerdown', function (e) {
          heroPtrs++;
          if (heroPtrs > 1) { pinchAbort(); return; }
          dragging = true; moved = 0; startX = e.clientX; startPos = target;
          velX = 0; lastVX = e.clientX; lastVT = e.timeStamp;
          try { elTitle.setPointerCapture(e.pointerId); } catch (err) {}
        });
        elTitle.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          var dx = e.clientX - startX;
          moved = Math.max(moved, Math.abs(dx));
          if (moved >= 8) retireSwipeHint();
          velTrack(e);
          target = startPos - dx / stagePx();
          spinTarget = Math.max(-0.5, Math.min(0.5, -dx / W()));
        });
        elTitle.addEventListener('pointerup', function () {
          heroPtrs = Math.max(0, heroPtrs - 1);
          if (!dragging) return;
          dragging = false; spinTarget = 0;
          if (moved < 8) {
            target = active;
            /* tapping the title enters the collection/product (Adam, 2026-08-28) */
            heroNav(products[norm(active)].url);
            return;
          }
          setActive(Math.round(target) + flickExtra());   /* swatch rules, same as the stage */
        });
        elTitle.addEventListener('pointercancel', function () {
          heroPtrs = Math.max(0, heroPtrs - 1);
          release(true);
        });
      }

      stage.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;  /* page keeps vertical */
        e.preventDefault();
        target += wheelHats(e.deltaX);                         /* no clamp — wraps */
        clearTimeout(stage._c3dWheelT);
        stage._c3dWheelT = setTimeout(function () { target = Math.round(target); setActive(target); }, 120);
      }, { passive: false });

      stage.tabIndex = 0;
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { setActive(active + 1); e.preventDefault(); }
        if (e.key === 'ArrowLeft') { setActive(active - 1); e.preventDefault(); }
      });
      /* GLASS ARROWS on the ring (Adam, 2026-09-02: "on all carousels left
         and right add a click arrow button that moves one hat in each
         direction on mobile and desktop") — rendered by the section, one
         hat per click from wherever the ring is heading, so rapid clicks
         add up. Swiping and the wheel are untouched. */
      Array.prototype.forEach.call(root.querySelectorAll('[data-c3d-arrow]'), function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          retireSwipeHint();
          setActive(Math.round(target) + (b.getAttribute('data-c3d-arrow') === 'next' ? 1 : -1));
        });
      });

      /* a tapped dot travels the SHORT way around the ring; tapping the dot
         that is ALREADY front goes to its collection (Adam). The target is
         computed ABSOLUTELY from the live ring position — a duplicated or
         stale actuation re-lands on the same dot instead of jumping twice
         (a double-jump was observed in pane testing, 2026-08-16). */
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var k = +t.dataset.i;
          var base = Math.round(pos);
          if (k === norm(base) && k === norm(active)) { heroNav(products[k].url); return; }
          setActive(base + sdelta(k - norm(base)));
        });
      });

      /* the swatch ring is itself swipeable — same drag physics as the stage,
         54px of finger per dot; a sub-8px touch falls through to the dot tap */
      var rail = root.querySelector('.c3d-thumbs');
      if (rail) {
        /* Magic Mouse / trackpad horizontal scroll drives the swatches
           too (Adam, 2026-08-28: "i cant swipe on the swatches with my
           magic mouse on desktop") — same wheel scrub the stage has */
        rail.addEventListener('wheel', function (e) {
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
          e.preventDefault();
          target += wheelHats(e.deltaX);
          clearTimeout(rail._c3dWheelT);
          rail._c3dWheelT = setTimeout(function () { target = Math.round(target); setActive(target); }, 120);
        }, { passive: false });
        /* SWATCH SWIPE (Adam, 2026-08-28: "make swiping and clicking much
           easier on the main swatches", then "more sensitive").
           It used to divide by 52 — the same 52px the dots are SPACED at —
           so the ring tracked the finger exactly 1:1 and a full 52px of
           travel bought exactly one dot. Decoupling the two is the whole
           fix: the dots still SIT 52px apart, but 26px of finger now moves
           one, and a flick carries further. */
        var RAIL_PX_PER_DOT = 26;   /* was 52 (1:1 with dot spacing) */
        var RAIL_PX_PER_DOT_DESK = 60;   /* desktop mouse drag, 2026-09-02 ("still quick on desktop") */
        function railPx() { return deskMQ.matches ? RAIL_PX_PER_DOT_DESK : RAIL_PX_PER_DOT; }
        var rDrag = false, rStartX = 0, rStartPos = 0, rMoved = 0;
        var rLastX = 0, rLastT = 0, rVel = 0;
        rail.addEventListener('pointerdown', function (e) {
          rDrag = true; rMoved = 0; rStartX = e.clientX; rStartPos = target;
          rLastX = e.clientX; rLastT = e.timeStamp || performance.now(); rVel = 0;
        });
        rail.addEventListener('pointermove', function (e) {
          if (!rDrag) return;
          var dx = e.clientX - rStartX;
          rMoved = Math.max(rMoved, Math.abs(dx));
          if (rMoved >= 6) retireSwipeHint();
          var now = e.timeStamp || performance.now();
          var dt = now - rLastT;
          if (dt > 0) rVel = Math.max(-4, Math.min(4, (e.clientX - rLastX) / dt));  /* px/ms, clamped */
          rLastX = e.clientX; rLastT = now;
          target = rStartPos - dx / railPx();
        });
        function railRelease() {
          if (!rDrag) return;
          rDrag = false;
          if (rMoved >= 6) {
            /* a flick keeps going: fast release throws 1-3 extra dots the
               way the finger was travelling, so browsing a long colour row
               doesn't need a drag per dot */
            var extra = 0;
            if (Math.abs(rVel) > (deskMQ.matches ? 0.6 : 0.25)) {
              extra = Math.min(3, Math.round(Math.abs(rVel) * 1.6)) * (rVel < 0 ? 1 : -1);
            }
            target = Math.round(target) + extra;
            setActive(target);
          }
        }
        rail.addEventListener('pointerup', railRelease);
        rail.addEventListener('pointercancel', railRelease);
        rail.addEventListener('click', function (e) {
          if (rMoved >= 6) { e.stopPropagation(); e.preventDefault(); }
        }, true);
      }

      function setActive(i) {
        if (norm(i) === norm(active) && Math.abs(target - i) < 0.01) return;
        retireSwipeHint();         /* any product change proves they can browse */
        unflip(norm(active));      /* leaving a card always restores its front */
        active = i; target = i;    /* unbounded — the ring wraps */
        paintCopy(norm(i), gsap);
        /* make sure the new neighbourhood's textures are in (they may still
           be waiting on the idle loader) */
        [norm(i - 1), norm(i), norm(i + 1)].forEach(function (idx) {
          if (cards[idx]) cards[idx].loadTex();
        });
        if (windowed) loadWindow(norm(i));   /* keep five ready either way */
      }

      /* render loop — paused off screen and on hidden tabs */
      var visible = true, last = performance.now(), raf = 0;
      var io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !raf) tick();
      }, { threshold: 0 });
      io.observe(stage);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden && visible && !raf) tick();
      });

      function tick() {
        if (contextDead) { raf = 0; return; }   /* GPU gone — static hero owns the stage now */
        if (!visible || document.hidden) { raf = 0; return; }
        var now = performance.now();
        var dt = Math.min(0.05, (now - last) / 1000); last = now;
        layout(dt, now);
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      }
      paintCopy(norm(active), null);   /* boot: the initial product must pass
        through updateTapHint too — setActive(0) early-returns at startup, which
        left the server-rendered tap hint visible on no-underbill products */
      tick();

      window.addEventListener('resize', function () {
        updShift();
        renderer.setSize(stage.clientWidth, stage.clientHeight);
        fitCamera();
      });

      /* no timed hide (Adam, 2026-08-27: "that is going away before a tap
         or click and it shouldn't") — the hint retires only on a real
         interaction: drag start, a flip, or the 3D going static. */

      /* micro animation: ladder tiles rise in on load. NO ScrollTrigger here —
         on desktop the ladder sits at the very top of the page, so a
         trigger-gated from() can arm already-past and leave the tiles stuck
         at opacity 0 (bug, 2026-08-16). A plain tween always completes. */
      var tiles = root.querySelectorAll('.c3d-tier');
      if (tiles.length) {
        gsap.from(tiles, { y: 18, autoAlpha: 0, duration: .5, stagger: .07, ease: 'power2.out', delay: .15 });
      }

      /* teardown for the theme editor */
      root._c3dDestroy = function () {
        io.disconnect();
        cancelAnimationFrame(raf); raf = 0; visible = false;
        renderer.dispose();
      };
    }).catch(goStatic);
  }

  /* ---------------------------------------------------------- SCROLL FX
     CENTER-STAGE FADE (Adam, 2026-08-16, replacing the Editions curtain the
     same day — "very clunky... I want it to fade in and out at the top so
     what is in the center of the screen is most visible while scrolling").

     No pinning, no covering. Every section carries a scrubbed opacity
     envelope keyed to VIEWPORT POSITION: it fades up as its top rises
     through the lower third, is fully visible while it holds the middle of
     the screen, and fades down as its bottom exits through the upper third.
     Tall sections stay solid for their whole middle — only the edges of the
     viewport are ever dim. Scrub means reversing scroll reverses the fade.

     Transform + opacity only; skipped under prefers-reduced-motion (guarded
     at the call site). */
  function initScrollFX(libs) {
    if (window.__c3dFX) return;
    window.__c3dFX = true;
    var gsap = libs.gsap;
    var secs = gsap.utils.toArray('#main-content > .shopify-section');

    secs.forEach(function (sec, i) {
      /* THE RENDERED CONTENT CHILD, not firstElementChild (bug, 2026-08-16):
         most sections open with a <link>/<script> (stylesheet_tag etc.), so
         firstElementChild is a NON-RENDERING element — the tweens ran, the
         computed opacity even read back "correctly", and nothing on screen
         faded. Adam caught it ("there is no fade at all"). Target the first
         child that actually paints. */
      var inner = null;
      for (var c = 0; c < sec.children.length; c++) {
        var tag = sec.children[c].tagName;
        if (tag !== 'LINK' && tag !== 'SCRIPT' && tag !== 'STYLE' && tag !== 'NOSCRIPT') {
          inner = sec.children[c];
          break;
        }
      }
      if (!inner || sec.offsetHeight < 40) return;   /* empty app shells etc. */
      var isHero = !!sec.querySelector('[data-c3d-hero]');

      /* fade IN while the section's top climbs toward the middle
         (the hero is already on screen at load — it only fades out) */
      if (!isHero) {
        gsap.fromTo(inner, { autoAlpha: 0.15, y: 26 }, {
          autoAlpha: 1, y: 0, ease: 'none',
          scrollTrigger: { trigger: sec, start: 'top 96%', end: 'top 52%', scrub: 0.3 }
        });
      }

      /* fade OUT at the top, while the section's bottom leaves the middle.
         immediateRender:false keeps this from stomping the entry tween's
         start state on load. */
      gsap.fromTo(inner, { autoAlpha: 1, y: 0 }, {
        autoAlpha: 0.15, y: -26, ease: 'none', immediateRender: false,
        scrollTrigger: { trigger: sec, start: 'bottom 48%', end: 'bottom 6%', scrub: 0.3 }
      });

      /* lifestyle photos keep their slow parallax breath */
      if (sec.className.indexOf('cc-image-banner') !== -1) {
        var img = sec.querySelector('img');
        if (img) {
          var clip = img.closest('div') || inner;
          clip.style.overflow = 'hidden';
          gsap.fromTo(img, { scale: 1 }, {
            scale: 1.07, ease: 'none',
            scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        }
      }
    });
  }

  /* -------------------------------------------------------------- UNDERBILL */
  function initUnderbill(root) {
    if (root.dataset.c3dUbInit) return;
    root.dataset.c3dUbInit = '1';

    var flips = Array.prototype.slice.call(root.querySelectorAll('.c3d-flip'));
    /* collection pages carry no story cards but still need the whole
       glass engine — they opt in with data-c3d-glass on the same root
       that carries data-c3d-underbill (Adam, 2026-08-17: the collection
       page shops through the glass too) */
    if (!flips.length && !root.hasAttribute('data-c3d-glass')) return;

    /* (the tap-to-flip mechanic and the FREEDOM scroll demo were removed
       2026-08-17 at Adam's call — the card face is the 3-hat composite
       now, which already shows the underbill. Cards are pictures + CTA.) */

    /* THE GLASS SHOP (Adam, 2026-08-17: "lets let them shop from here"):
       tapping the category button blurs the page and opens a THREE-LEVEL
       glass browser — collections → styles → hats — with a back button
       and an X on every level. Styles always appear in Adam's order
       (only the ones the collection actually has). A hat tile carries an
       ADD TO CART button; tapping the tile itself goes to the product
       page. Product data comes from the storefront's own
       /collections/<handle>/products.json in the buyer's browser — no
       tokens, no baked lists. (The drip-down chevron was removed the same
       day: the shop replaced it.) */
    /* Adam's fixed order, 2026-08-17: "this order always: flat bill,
       baseball hat fit, shallow fit, dad hat, trucker, sporty". Low
       Profile stays in the roster (13 live products still carry it)
       slotted after its successor Shallow Fit. */
    var STYLE_ORDER = ['Flat Bill', 'Baseball Hat Fit', 'Shallow Fit', 'Low Profile', 'Dad Hat', 'Trucker', 'Sporty'];
    /* some collections break into SUBSECTIONS instead of styles (Adam,
       2026-08-17). First matching rule wins; unmatched products land in
       More — unless noMore, where Adam wants ONLY the named groups shown
       (straw: "only the straw hats in those categories"). Color Vibes
       grouping is by COLOR TYPE, Adam's brief: "neons, pastels, however
       you think you can group them". */
    var SUBSECTIONS = {
      'flag-hats': { groups: [
        { label: 'USA', match: /usa/i },
        { label: 'FREEDOM', match: /freedom/i },
        { label: 'Color Vibes Flag', match: /color vibes/i },
        { label: 'Classic Flag', match: /flag/i }
      ] },
      /* claude- (2026-08-27, Adam): color vibes PDP swatches show ALL the
         hats flat — "just show all of the hats in the swatches not flipping
         through them … don't break them down" — in exactly TWO never-mixing
         segments: "don't have youth swatches inside the adult product".
         First-match-wins keeps Adult youth-free. The color-type breakdown
         (neons/pastels/…) retired here 2026-08-27. */
      'color-vibes-hats': { noCross: true, groups: [
        { label: 'Youth', match: /^\s*\**\s*youth/i },
        { label: 'Adult', match: /./ }
      ] },
      'color-vibes-flat-bill-hats-youth': { noCross: true, groups: [
        { label: 'Youth', match: /./ }
      ] },
      'color-vibes-flat-bill-hats-adult': { noCross: true, groups: [
        { label: 'Adult', match: /./ }
      ] },
      'straw-hats': { noMore: true, groups: [
        { label: 'Flag', match: /flag/i },
        { label: 'Florida', match: /florida/i },
        { label: 'Tahoe', match: /tahoe/i },
        { label: 'Idaho', match: /idaho/i },
        { label: 'California', match: /california/i }
      ] }
    };
    /* collections that skip grouping entirely — every hat is its own
       tile (Adam: "freedom will be broken up by tile") */
    /* collections that skip the style breakup and go straight to a
       scrolling product grid — freedom (by tile), and hat ropes, whose
       titles match no style so the breakup showed one useless "More"
       tile (Adam, 2026-08-17: "that should just be scrolling to
       purchase") */
    var DIRECT_TILES = { 'freedom-hats': true, 'hat-ropes': true };
    var colCache = {};
    var lastCol = null;
    var ovStack = [];

    function fetchCollection(handle) {
      /* one retry with a beat between — a burst of walls can trip
         Shopify's products.json throttling, and a single 429 must NOT
         dump the shopper out of the glass (2026-08-17: it did) */
      handle = COL_ALIAS[handle] || handle;
      if (colCache[handle]) {
        lastCol = { handle: handle, prods: colCache[handle] };
        return Promise.resolve(colCache[handle]);
      }
      function attempt() {
        return fetch('/collections/' + handle + '/products.json?limit=250')
          .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
          .then(function (d) {
            colCache[handle] = d.products || [];
            /* remember the collection being BROWSED — the PDP's
               other-styles carousels are built from it */
            lastCol = { handle: handle, prods: colCache[handle] };
            return colCache[handle];
          });
      }
      return attempt().catch(function () {
        return new Promise(function (res) { setTimeout(res, 700); }).then(attempt);
      });
    }
    /* tile PICTURES use a light, queued fetch (4 products, 3 at a time)
       so a wall of twenty tiles doesn't burst-trip the throttle */
    var imgCache = {};
    var imgQueue = [], imgActive = 0;
    /* the menu's TEAM HEROES & CAUSES link points at /collections/
       team-heroes, which does not exist — the real collection is
       team-heroes-hats (its tile had no photo, Adam 2026-08-17) */
    var COL_ALIAS = { 'team-heroes': 'team-heroes-hats' };
    function fetchCollectionLite(handle) {
      handle = COL_ALIAS[handle] || handle;
      if (colCache[handle]) return Promise.resolve(colCache[handle]);
      if (imgCache[handle]) return Promise.resolve(imgCache[handle]);
      return new Promise(function (resolve) {
        imgQueue.push({ h: handle, res: resolve });
        pumpImgQueue();
      });
    }
    function pumpImgQueue() {
      while (imgActive < 3 && imgQueue.length) {
        (function (job) {
          imgActive++;
          fetch('/collections/' + job.h + '/products.json?limit=4')
            .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
            .then(function (d) { imgCache[job.h] = d.products || []; job.res(imgCache[job.h]); })
            .catch(function () { job.res([]); })
            .then(function () { imgActive--; pumpImgQueue(); });
        })(imgQueue.shift());
      }
    }
    function imgOk(s) { s = s.toLowerCase(); return s.indexOf('underbill') < 0 && s.indexOf('featureicon') < 0; }
    /* claude- (2026-08-24): products whose photos have baked white backgrounds
       carry transparent cutouts as theme assets — the section stamps the map on
       the root (data-claude-cuts, handle -> url) and every client-side pick
       (PDP swatches, walls) prefers it. */
    var claudeCutMap = null;
    function cutFor(handle) {
      if (claudeCutMap === null) {
        try { claudeCutMap = JSON.parse(root.getAttribute('data-claude-cuts') || '{}'); }
        catch (e) { claudeCutMap = {}; }
      }
      return claudeCutMap[handle] || null;
    }
    function prodImgSrc(p) {
      var cut = p.handle && cutFor(p.handle);
      if (cut) return cut;
      var imgs = p.images || [], i, s;
      for (i = 0; i < imgs.length; i++) { s = imgs[i].src; if (imgOk(s) && s.toLowerCase().indexOf('main') >= 0) return s; }
      for (i = 0; i < imgs.length; i++) { s = imgs[i].src; if (imgOk(s) && s.toLowerCase().indexOf('front') >= 0) return s; }
      return imgs.length ? imgs[0].src : '';
    }
    function styleOf(p) {
      var t = p.title.toLowerCase();
      for (var i = 0; i < STYLE_ORDER.length; i++) {
        if (t.indexOf(STYLE_ORDER[i].toLowerCase()) >= 0) return STYLE_ORDER[i];
      }
      /* Adam, 2026-08-17: "airmesh can always be low profile as well" */
      if (/air ?mesh/i.test(t)) return 'Low Profile';
      return 'More';
    }
    function firstAvail(p) {
      var vs = p.variants || [];
      for (var i = 0; i < vs.length; i++) if (vs[i].available) return vs[i];
      return vs[0] || null;
    }
    function cartAdd(variantId) {
      /* The cart popout on this store is the UPCART APP, and it hooks
         XHR as well as fetch — it opens on every add no matter the
         transport (learned 2026-08-17). That's fine: Adam wants it
         opening at HALF height (CSS above), with the rope dialog
         centered on top of it. */
      return new Promise(function (resolve, reject) {
        var x = new XMLHttpRequest();
        x.open('POST', '/cart/add.js');
        x.setRequestHeader('Content-Type', 'application/json');
        x.onload = function () {
          if (x.status >= 200 && x.status < 300) resolve();
          else reject(new Error('cart ' + x.status));
        };
        x.onerror = function () { reject(new Error('network')); };
        x.send(JSON.stringify({ id: variantId, quantity: 1 }));
      });
    }
    /* the collection-page hero's ADD TO CART lives in initHero, outside
       this closure — same add, same UpCart popout */
    window.__c3dCartAdd = cartAdd;
    /* GLASS NAMES ARE NOT SEO TITLES (Adam, 2026-08-17: "in the glass we
       are going to make it a simpler way of life... adjust the naming of
       each hat in the glass to be different than what we have as the
       title for the product in seo"). The product titles never change —
       search keeps its keywords — but inside the glass every hat wears a
       short display name derived here: the type segment goes (the shopper
       is already inside the category), the style goes when the list is a
       single style, and the technical tail is dropped entirely. ALL glass
       naming flows through this one function — simplifying life further
       is a one-place edit. */
    /* the name carries the colorway — and a significant FABRIC (hemp,
       suede, corduroy, cork) stays IN the name where the title puts it,
       e.g. "Flat Bill · Cork · Black" (Adam, 2026-08-17, revising the
       separate fabric line). Only the MESH gets its own line underneath. */
    function glassNameOf(p, styleName) {
      var parts = p.title.split(' - ');
      var out = [], mesh = null;
      /* adult and youth versions of the same print must stay
         distinguishable once the type segment is dropped */
      if (/\*\*\s*youth\s*\*\*/i.test(parts[0])) out.push('Youth');
      for (var i = 1; i < parts.length; i++) {
        var seg = parts[i].trim();
        if (!seg) continue;
        if (/^(laser mesh|trucker mesh|air ?mesh|mesh)$/i.test(seg)) { mesh = seg; continue; }
        if (/^(water resistant|performance|snapback|true fit)$/i.test(seg)) continue;
        /* strip the disclaimer but KEEP the rest of the segment —
           skipping the whole segment left "White (Hat Not Included)"
           nameless (fell back to the raw title) and ate "Waves" */
        seg = seg.replace(/\(\s*hat not included\s*\)/i, '').trim();
        if (!seg) continue;
        /* the STYLE stays, always first (Adam, 2026-08-17: "the first
           part of all of the titles should be the style") — it lands
           first naturally once the type segment is gone */
        out.push(seg);
      }
      return {
        name: out.length ? out.join(' · ') : p.title,
        mesh: mesh
      };
    }
    function colorwayOf(p, styleName) { return glassNameOf(p, styleName).name; }

    function ovEl() {
      var ov = document.querySelector('.c3d-ovl');
      if (ov) return ov;
      ov = document.createElement('div');
      ov.className = 'c3d-ovl';
      ov.innerHTML = '<button type="button" class="c3d-ovl__back" aria-label="Back">&#8249;</button>'
        + '<p class="c3d-ovl__title" aria-hidden="true"></p>'
        + '<button type="button" class="c3d-ovl__close" aria-label="Close">&#215;</button>'
        + '<div class="c3d-ovl__grid"></div>';
      document.body.appendChild(ov);
      ov.querySelector('.c3d-ovl__close').addEventListener('click', closeOverlay);
      ov.querySelector('.c3d-ovl__back').addEventListener('click', function () {
        if (ovStack.length > 1) {
          /* stepping back off a glass PDP releases its URL; if the previous
             level is itself a product, its renderProduct re-enters */
          glassUrlExit();
          ovStack.pop(); ovStack[ovStack.length - 1]();
        }
      });
      ov.addEventListener('click', function (e) {
        if (e.target === ov || e.target.classList.contains('c3d-ovl__grid')) closeOverlay();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeOverlay(); });
      return ov;
    }
    /* claude- (2026-08-27, Adam: "when i am selecting a product and viewing
       in the glass … it is hitting that products page for all purposes"):
       a glass PDP open PUSHES the real /products/<handle> URL, so every
       URL-keyed tool — GA/GTM history triggers, pixels, Hotjar, shares, a
       refresh — sees the product page, alongside the Klaviyo + Shopify
       Analytics events glassTrackProduct already fires. Swatch hops
       REPLACE (no history spam); browser back and the overlay's close both
       restore the page the shopper was on. */
    var glassPrevTitle = null;
    var glassPopClosing = false;
    function glassUrlEnter(p) {
      try {
        var target = '/products/' + p.handle + window.location.search;
        if (window.location.pathname === '/products/' + p.handle) return;
        if (history.state && history.state.c3dGlass) {
          history.replaceState({ c3dGlass: p.handle }, '', target);
        } else {
          glassPrevTitle = document.title;
          history.pushState({ c3dGlass: p.handle }, '', target);
        }
        document.title = p.title + ' | The Heartbeat Brand';
      } catch (e) {}
    }
    function glassUrlExit() {
      try {
        if (history.state && history.state.c3dGlass) {
          glassPopClosing = true;
          history.back();
        }
        if (glassPrevTitle) { document.title = glassPrevTitle; glassPrevTitle = null; }
      } catch (e) {}
    }
    window.addEventListener('popstate', function (e) {
      if (glassPopClosing) { glassPopClosing = false; return; }
      /* browser back while a glass product URL is up: close the overlay
         instead of leaving the page */
      if (!(e.state && e.state.c3dGlass)) {
        if (glassPrevTitle) { document.title = glassPrevTitle; glassPrevTitle = null; }
        var ovp = document.querySelector('.c3d-ovl.is-open');
        if (ovp) closeOverlay(true);
      }
    });
    function closeOverlay(fromPop) {
      var ov = document.querySelector('.c3d-ovl');
      if (!ov) return;
      if (fromPop !== true) glassUrlExit();
      hideRopeSheet();
      searchChrome(false);   /* the glass-search offset dies with the wall */
      ovStack = [];
      ov.classList.remove('is-in');
      ov.querySelector('.c3d-ovl__grid').classList.remove('is-in');
      clearTimeout(ov._t);
      ov._t = setTimeout(function () { ov.classList.remove('is-open'); }, 380);
      document.documentElement.classList.remove('c3d-ovl-lock');
    }
    /* claude- (2026-08-27, Adam: "all states also change to States & Places
       everwhere even in the menu" — scrolling header exempted): a DISPLAY
       rename only. Matching (walls, MENU_TILE_IMG, NAV_WALL_*) keeps the
       nav's raw 'States' key. */
    function dispLabel(l) {
      if (/^states$/i.test(String(l || '').trim())) return 'States & Places';
      return l;
    }
    function paintGrid(build, mode, title) {
      var ov = ovEl();
      /* the level's name reads across the top — the category itself is
         just a word up there, never a tile (Adam, 2026-08-17) */
      ov.querySelector('.c3d-ovl__title').textContent = dispLabel(title) || '';
      var g = ov.querySelector('.c3d-ovl__grid');
      g.classList.remove('is-in');
      g.classList.toggle('c3d-ovl__grid--shop', mode === true || mode === 'shop');
      g.classList.toggle('c3d-ovl__grid--pdp', mode === 'pdp');
      g.classList.toggle('c3d-ovl__grid--menu', mode === 'menu');
      g.innerHTML = '';
      g.scrollTop = 0;
      build(g);
      /* shrink any label that would overrun its budget — nothing ever
         lies outside the box (Adam, 2026-08-17) */
      Array.prototype.slice.call(g.querySelectorAll('.c3d-ovl__chiplabel')).forEach(function (el) {
        var size = 15;
        el.style.fontSize = '';
        while (size > 9 && el.scrollHeight > 60) {
          size -= 1;
          el.style.fontSize = size + 'px';
        }
      });
      ov.querySelector('.c3d-ovl__back').style.display = ovStack.length > 1 ? '' : 'none';
      /* EVEN SPACING (Adam, 2026-08-17: "thats not even spacing"): a
         scrolling grid sizes each row to its own content, so tiles with
         a 3-line name, a notify block or no reviews made ragged rows.
         Measure the tallest shop tile and give every tile that height —
         once when the wall paints, again after Judge.me badges and lazy
         images have settled. */
      function evenPass() {
        var chips = g.querySelectorAll('.c3d-ovl__chip--shop');
        if (chips.length < 2) return;
        /* per-ROW label zones first (Adam, 2026-08-17: "just left to
           right so you can look at each line as a group" — a short name
           must not inherit a 3-line gap from a different row) */
        Array.prototype.forEach.call(chips, function (c) {
          c.style.minHeight = '';
          var lb = c.querySelector('.c3d-ovl__chiplabel');
          if (lb) lb.style.minHeight = '';
        });
        var rows = {};
        Array.prototype.forEach.call(chips, function (c) {
          var k = Math.round(c.getBoundingClientRect().top / 8);
          (rows[k] = rows[k] || []).push(c);
        });
        Object.keys(rows).forEach(function (k) {
          var lmax = 0;
          rows[k].forEach(function (c) {
            var lb = c.querySelector('.c3d-ovl__chiplabel');
            if (lb && lb.offsetHeight > lmax) lmax = lb.offsetHeight;
          });
          rows[k].forEach(function (c) {
            var lb = c.querySelector('.c3d-ovl__chiplabel');
            if (lb) lb.style.minHeight = lmax + 'px';
          });
        });
        var max = 0;
        Array.prototype.forEach.call(chips, function (c) {
          if (c.offsetHeight > max) max = c.offsetHeight;
        });
        Array.prototype.forEach.call(chips, function (c) { c.style.minHeight = max + 'px'; });
      }
      /* the glass SEARCH wall swaps its result chips in place as the
         query changes — it re-runs this same pass on the fresh chips */
      g.__evenPass = evenPass;
      setTimeout(evenPass, 450);
      setTimeout(evenPass, 1500);
      clearTimeout(ov._t);
      if (!ov.classList.contains('is-open')) {
        ov.classList.add('is-open');
        document.documentElement.classList.add('c3d-ovl-lock');
      }
      /* two frames so the resting state paints before the pop begins */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { ov.classList.add('is-in'); g.classList.add('is-in'); });
      });
    }
    /* WHITE BACKGROUNDS DIE AT RUNTIME (Adam, 2026-08-20: "all of the photos
       need the same treatment if they have the white background — for
       example boonie white camo"). Any glass image that loads with white
       corners is flood-filled transparent on a canvas and swapped in place —
       catalog shots, current and future, no baked assets needed. Fill walks
       in from the BORDER only, so white patches ON a hat survive. Images
       already transparent fail the corner test and pass through untouched.
       Shopify's CDN sends CORS headers; a tainted canvas (any other origin)
       bails silently and the original photo stays. */
    function deWhite(im) {
      if (im.__c3dDW) return;
      im.__c3dDW = 1;
      if (!im.crossOrigin) im.crossOrigin = 'anonymous';
      var run = function () {
        var W0 = im.naturalWidth, H0 = im.naturalHeight;
        if (!W0 || !H0) return;
        var W = Math.min(W0, 480), H = Math.max(1, Math.round(H0 * W / W0));
        var cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(im, 0, 0, W, H);
        var d;
        try { d = cx.getImageData(0, 0, W, H); } catch (e) { return; }
        var px = d.data;
        function white(i2) {
          return px[i2 + 3] > 0 && px[i2] >= 235 && px[i2 + 1] >= 235 && px[i2 + 2] >= 235;
        }
        var corners = [(1 * W + 1) * 4, (1 * W + (W - 2)) * 4,
                       ((H - 2) * W + 1) * 4, ((H - 2) * W + (W - 2)) * 4];
        var wc = 0;
        corners.forEach(function (c) { if (white(c)) wc++; });
        if (wc < 3) return;
        var visited = new Uint8Array(W * H);
        var stack = [];
        var x, y;
        for (x = 0; x < W; x++) { stack.push(x, 0, x, H - 1); }
        for (y = 0; y < H; y++) { stack.push(0, y, W - 1, y); }
        while (stack.length) {
          y = stack.pop(); x = stack.pop();
          if (x < 0 || y < 0 || x >= W || y >= H) continue;
          var n = y * W + x;
          if (visited[n]) continue;
          visited[n] = 1;
          var i2 = n * 4;
          if (!white(i2)) continue;
          px[i2 + 3] = 0;
          stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
        }
        /* POCKETS (Adam, 2026-08-20: "white camo still has some white where
           the neck strap is") — background showing THROUGH a loop is sealed
           off from the border, so the walk above never reaches it. Second
           pass: clear a leftover white region only when it is big AND pure
           white throughout — a printed patch or a highlight has shading and
           fails the purity bar, so whites ON the hat still survive. */
        var n2, i3, cxx, cyy;
        for (n2 = 0; n2 < W * H; n2++) {
          if (visited[n2]) continue;
          i3 = n2 * 4;
          if (!white(i3)) continue;
          var comp = [], hi = 0;
          var st2 = [n2 % W, (n2 / W) | 0];
          while (st2.length) {
            cyy = st2.pop(); cxx = st2.pop();
            if (cxx < 0 || cyy < 0 || cxx >= W || cyy >= H) continue;
            var m = cyy * W + cxx;
            if (visited[m]) continue;
            visited[m] = 1;
            var i4 = m * 4;
            if (!white(i4)) continue;
            comp.push(i4);
            if (px[i4] >= 246 && px[i4 + 1] >= 246 && px[i4 + 2] >= 246) hi++;
            st2.push(cxx + 1, cyy, cxx - 1, cyy, cxx, cyy + 1, cxx, cyy - 1);
          }
          /* studio background is BLOWN white (>=246); fabric white never is.
             A big region that is mostly blown = background through a loop,
             even with a soft shadow ramp at its rim. */
          if (comp.length >= 400 && hi / comp.length >= 0.8) {
            comp.forEach(function (i5) { px[i5 + 3] = 0; });
          }
        }
        /* nibble the shadow ring a cleared pocket leaves behind: two thin
           dilation passes eating dim gray pixels that touch transparency */
        var pass, k, moved;
        for (pass = 0; pass < 2; pass++) {
          moved = [];
          for (k = 0; k < W * H; k++) {
            i3 = k * 4;
            if (px[i3 + 3] === 0) continue;
            if (px[i3] < 200 || px[i3 + 1] < 200 || px[i3 + 2] < 200) continue;
            x = k % W; y = (k / W) | 0;
            var t1 = x > 0 && px[(k - 1) * 4 + 3] === 0;
            var t2 = x < W - 1 && px[(k + 1) * 4 + 3] === 0;
            var t3 = y > 0 && px[(k - W) * 4 + 3] === 0;
            var t4 = y < H - 1 && px[(k + W) * 4 + 3] === 0;
            if (t1 || t2 || t3 || t4) moved.push(i3);
          }
          moved.forEach(function (i5) { px[i5 + 3] = 0; });
        }
        cx.putImageData(d, 0, 0);
        try { im.src = cv.toDataURL('image/png'); } catch (e) {}
      };
      if (im.complete && im.naturalWidth) {
        /* already loaded — possibly without CORS; reload a clean copy,
           cut on that, hand the result back to the DOM node */
        var clean = new Image();
        clean.crossOrigin = 'anonymous';
        clean.onload = function () {
          var keep = im;
          im = clean;
          run();
          keep.src = clean.src;   /* dataURL when cut; original otherwise */
        };
        clean.src = im.src;
      } else {
        if (im.src) { var s = im.src; im.src = ''; im.src = s; }  /* restart the fetch WITH CORS */
        im.addEventListener('load', function once() {
          im.removeEventListener('load', once);
          run();
        });
      }
    }

    function chip(g, i, opts) {
      var el = document.createElement(opts.href && !opts.cart && !opts.onPick ? 'a' : 'div');
      el.className = 'c3d-ovl__chip';
      if (el.tagName === 'A') el.href = opts.href;
      if (opts.img) {
        var im = document.createElement('img');
        im.className = 'c3d-ovl__chipimg';
        im.crossOrigin = 'anonymous';
        im.src = opts.img; im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        deWhite(im);
        el.appendChild(im);
      }
      var sp = document.createElement('span');
      sp.className = 'c3d-ovl__chiplabel';
      sp.textContent = dispLabel(opts.label);
      el.appendChild(sp);
      (opts.fabLines || []).forEach(function (line) {
        var f = document.createElement('span');
        f.className = 'c3d-ovl__fabline';
        f.textContent = line;
        el.appendChild(f);
      });
      /* shop tiles RESERVE the mesh row even when there isn't one, so
         stars / price / buttons land on the same line in every tile
         (Adam, 2026-08-17: "everything must line up") */
      if (opts.cart && (!opts.fabLines || !opts.fabLines.length)) {
        var f0 = document.createElement('span');
        f0.className = 'c3d-ovl__fabline';
        f0.innerHTML = '&nbsp;';
        el.appendChild(f0);
      }
      if (opts.jdgmHandle) {
        /* Judge.me's own preview badge (stars + count) — filled after the
           level renders via jdgm.batchRenderBadges([{productHandle,
           badgePlaceholder}]), which is the shape its source expects */
        var jb = document.createElement('div');
        jb.className = 'c3d-ovl__stars';
        jb.setAttribute('data-product-handle', opts.jdgmHandle);
        el.appendChild(jb);
      }
      if (opts.sub) {
        var sb = document.createElement('span');
        sb.className = 'c3d-ovl__chipsub';
        if (opts.was) {
          /* struck compare-at beside the sale price (Adam, launch night) */
          sb.innerHTML = '<s class="c3d-pp-was">' + opts.was + '</s> ' + opts.sub;
        } else {
          sb.textContent = opts.sub;
        }
        el.appendChild(sb);
      }
      if (opts.mix) { el.appendChild(mixEl()); }
      if (opts.cart) {
        el.classList.add('c3d-ovl__chip--shop');
        if (!opts.cart.available && klaviyoCompanyId()) {
          /* sold out: a quiet flag plus NOTIFY ME — no dead button */
          var sf = document.createElement('span');
          sf.className = 'c3d-ovl__soldflag';
          sf.textContent = 'Sold out';
          el.appendChild(sf);
          el.appendChild(notifyControl(opts.cart.id));
        } else {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c3d-ovl__atc';
        if (opts.cart.available) { btn.textContent = 'Add to cart'; }
        else {
          btn.textContent = 'Sold out';
          btn.disabled = true;
        }
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          if (btn.disabled) return;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          cartAdd(opts.cart.id).then(function () {
            btn.textContent = 'Added ✓';
            btn.disabled = false;
            /* rope popup retired (Adam, 2026-08-26) — the cart drawer's
               Suggested Add-Ons unit replaced it; code kept for rollback */
          }).catch(function () {
            btn.textContent = 'Try again';
            btn.disabled = false;
          });
        });
        el.appendChild(btn);
        }
      }
      if (opts.onPick) {
        el.addEventListener('click', function (e) { e.preventDefault(); opts.onPick(); });
        el.style.cursor = 'pointer';
      } else if (opts.cart && opts.onOpen) {
        /* tapping the tile opens the GLASS product page — the shopper is
           never navigated away (Adam, 2026-08-17) */
        el.addEventListener('click', function () { opts.onOpen(); });
        el.style.cursor = 'pointer';
      } else if (opts.cart && opts.href) {
        el.addEventListener('click', function () { window.location.href = opts.href; });
        el.style.cursor = 'pointer';
      }
      /* one at a time — capped so a 38-hat level doesn't trickle for
         seconds while the shopper is already scrolling */
      el.style.transitionDelay = (40 + Math.min(i * 40, 480)) + 'ms';
      g.appendChild(el);
      return el;
    }
    function go(fn) { ovStack.push(fn); fn(); }

    /* the store's live volume ladder, under every glass price — MIX &
       MATCH headline above the numbers, both sized up (Adam, 2026-08-17) */
    function mixEl() {
      var mx = document.createElement('div');
      mx.className = 'c3d-ovl__mix';
      var h = document.createElement('span');
      h.className = 'c3d-ovl__mixhead';
      h.textContent = 'Mix & Match';
      var vals = document.createElement('span');
      vals.className = 'c3d-ovl__mixvals';
      vals.textContent = '2 for $70 · 3 for $110 · 4 for $140';   /* 2026-09-16: $39.99 hats, 2 for $70 on every pair */
      mx.appendChild(h);
      mx.appendChild(vals);
      return mx;
    }

    /* NOTIFY WHEN AVAILABLE (Adam, 2026-08-17): sold-out hats grow a
       notify button; the email goes to Klaviyo's back-in-stock API using
       the site's own public key, read off the Klaviyo script tag at
       runtime — nothing hardcoded. Klaviyo emails them when stock lands.
       If Klaviyo isn't on the page the button simply doesn't render. */
    function klaviyoCompanyId() {
      if (window.__c3dKco !== undefined) return window.__c3dKco;
      var id = null;
      Array.prototype.slice.call(document.querySelectorAll('script[src*="klaviyo"]')).forEach(function (s) {
        var m = /company_id=([A-Za-z0-9]+)/.exec(s.src || '');
        if (m) id = m[1];
      });
      window.__c3dKco = id;
      return id;
    }
    /* the collection page's grid builds the SAME sold-out notify block
       (Adam, 2026-08-17: "I want the product on all glass to mimic each
       other") — hoisting makes the function reachable from here */
    window.__c3dNotifyControl = function (variantId) {
      return klaviyoCompanyId() ? notifyControl(variantId) : null;
    };
    function notifyControl(variantId) {
      /* the whole thing stands open (Adam, 2026-08-17): Sold out flag
         above this, then the email field, then NOTIFY ME which submits */
      var wrap = document.createElement('div');
      wrap.className = 'c3d-ovl__notify';
      var inp = document.createElement('input');
      inp.type = 'email';
      inp.placeholder = 'Enter Email';   /* Adam's wording, 2026-08-17 */
      inp.className = 'c3d-ovl__notifyinput';
      inp.addEventListener('click', function (ev) { ev.stopPropagation(); });
      var send_ = document.createElement('button');
      send_.type = 'button';
      send_.className = 'c3d-ovl__atc';
      send_.textContent = 'Notify me';
      send_.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var email = inp.value.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { inp.classList.add('is-bad'); return; }
        inp.classList.remove('is-bad');
        send_.disabled = true;
        send_.textContent = '…';
        fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + klaviyoCompanyId(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify({ data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              channels: ['EMAIL']
            },
            relationships: { variant: { data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            } } }
          } })
        }).then(function (r) {
          if (r.status < 200 || r.status >= 300) throw new Error('klaviyo ' + r.status);
          wrap.innerHTML = '';
          var ok = document.createElement('span');
          ok.className = 'c3d-ovl__chipsub';
          ok.textContent = "You're on the list ✓";
          wrap.appendChild(ok);
        }).catch(function () {
          send_.disabled = false;
          send_.textContent = 'Try again';
        });
      });
      wrap.appendChild(inp);
      wrap.appendChild(send_);
      return wrap;
    }

    /* THE ROPE UPSELL (Adam, 2026-08-17): after any successful add-to-cart
       from the glass, a small glass sheet slides up offering a hat rope —
       a swipeable carousel of every rope in stock, NO THANKS bottom-right.
       "No thanks" silences it for the SESSION; adding a rope or ignoring
       it does not — it returns on the next add. The price in the title is
       the ropes' LIVE minimum price from the storefront, never hardcoded
       (they list at $5.99 as of 2026-08-17, not the $4.99 in the ask —
       flagged to Adam). */
    var ROPE_KEY = 'c3dRopeNoThanks';
    function maybeOfferRope() {
      try { if (window.sessionStorage.getItem(ROPE_KEY) === '1') return; } catch (e) {}
      fetchCollection('hat-ropes').then(function (ropes) {
        ropes = ropes.filter(function (p) { var v = firstAvail(p); return v && v.available; });
        if (ropes.length) showRopeSheet(ropes);
      }).catch(function () {});
    }
    function hideRopeSheet() {
      var s = document.querySelector('.c3d-rope');
      if (!s) return;
      s.classList.remove('is-in');
      setTimeout(function () { s.remove(); }, 380);
    }
    function showRopeSheet(ropes) {
      hideRopeSheet();
      var minP = null;
      ropes.forEach(function (p) {
        var n = parseFloat(firstAvail(p).price);
        if (minP === null || n < minP) minP = n;
      });
      var sheet = document.createElement('div');
      sheet.className = 'c3d-rope';
      var ttl = document.createElement('p');
      ttl.className = 'c3d-rope__title';
      ttl.textContent = 'Add a hat rope — $' + minP.toFixed(2);
      sheet.appendChild(ttl);
      var row = document.createElement('div');
      row.className = 'c3d-rope__row';
      ropes.forEach(function (p) {
        var v = firstAvail(p);
        var t = document.createElement('button');
        t.type = 'button';
        t.className = 'c3d-rope__tile';
        var im = document.createElement('img');
        im.src = prodImgSrc(p); im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        t.appendChild(im);
        var nm = document.createElement('span');
        nm.textContent = p.title
          .replace(/^Hat Rope - /i, '')
          .replace(/\s*\(Hat Not Included\)\s*/i, '');
        t.appendChild(nm);
        t.addEventListener('click', function () {
          if (t.classList.contains('is-busy')) return;
          t.classList.add('is-busy');
          nm.textContent = 'Adding…';
          cartAdd(v.id).then(function () {
            nm.textContent = 'Added ✓';
            setTimeout(hideRopeSheet, 750);
          }).catch(function () {
            nm.textContent = 'Try again';
            t.classList.remove('is-busy');
          });
        });
        row.appendChild(t);
      });
      sheet.appendChild(row);
      var no = document.createElement('button');
      no.type = 'button';
      no.className = 'c3d-rope__no';
      no.textContent = 'No thanks';
      no.addEventListener('click', function () {
        try { window.sessionStorage.setItem(ROPE_KEY, '1'); } catch (e) {}
        hideRopeSheet();
      });
      sheet.appendChild(no);
      /* body-level, not inside the overlay — the dialog centers on the
         SCREEN and outranks the theme's cart drawer */
      document.body.appendChild(sheet);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { sheet.classList.add('is-in'); });
      });
    }

    function renderProducts(prods, styleName, title) {
      paintGrid(function (g) {
        prods.forEach(function (p, i) {
          var v = firstAvail(p);
          var n = glassNameOf(p, styleName);
          chip(g, i, {
            label: n.name,
            fabLines: n.mesh ? [n.mesh] : [],
            sub: v ? '$' + v.price : '',
              was: v && parseFloat(v.compare_at_price || 0) > parseFloat(v.price || 0) ? '$' + v.compare_at_price : null,
            img: prodImgSrc(p),
            href: '/products/' + p.handle,
            jdgmHandle: p.handle,
            cart: v ? { id: v.id, available: !!v.available } : null,
            onOpen: function () { go(function () { renderProduct(p, prods, styleName); }); }
          });
        });
      }, true, title || styleName || '');
      /* light up the stars on the fresh tiles */
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
        try {
          var slots = Array.prototype.slice.call(
            document.querySelectorAll('.c3d-ovl .c3d-ovl__stars[data-product-handle]'));
          if (slots.length) {
            window.jdgm.batchRenderBadges(slots.map(function (el) {
              return { productHandle: el.getAttribute('data-product-handle'), badgePlaceholder: el };
            }));
          }
        } catch (e) {}
      }
    }
    /* THE GLASS SEARCH (Adam, 2026-08-27: "the entire search experience
       needs the glass treatment it is clunky old and hats photos with
       white backgrounds its messy" — then, same day: "it should still
       interact with the current search bar not be its own overlay").
       So: the THEME'S OWN search bar stays the input — it keeps focus,
       value and markup — and the glass only paints the RESULTS below it,
       as the same shop tiles every other wall uses (side shots, deWhite,
       stars, price, add to cart, tap → glass PDP). Nothing server-
       rendered changes: the form, the input, /search and the predictive
       markup are byte-identical for a crawler, and a crawler never
       focuses a field, so it never triggers any of this. Data comes from
       the search.claude-results JSON view, so images follow the house
       pick rules instead of the white featured shots. */
    var searchSeq = 0, searchT = null, searchLast = null, searchBar = null;
    function searchFetch(q) {
      return fetch('/search?q=' + encodeURIComponent(q) + '&type=product&view=claude-results')
        .then(function (r) { if (!r.ok) throw new Error('search ' + r.status); return r.json(); });
    }
    function searchChrome(on) {
      var ov = document.querySelector('.c3d-ovl');
      if (!ov) return;
      if (on) {
        ov.classList.add('c3d-ovl--search');
        var top = 0;
        try { top = Math.max(0, Math.round(searchBar.getBoundingClientRect().bottom) + 6); } catch (e) {}
        ov.style.top = top + 'px';
      } else {
        ov.classList.remove('c3d-ovl--search');
        ov.style.top = '';
        /* leaving search folds the bar's focus too, so the keyboard drops
           and the theme's search UI has no reason to wake up */
        if (searchBar) { try { searchBar.blur(); } catch (e) {} }
      }
    }
    function paintSearchResults(prods, msg) {
      var g = searchPaint._grid, note = searchPaint._note;
      if (!g || !note || !g.isConnected) return;
      Array.prototype.slice.call(g.children).forEach(function (c) {
        if (c !== note) g.removeChild(c);
      });
      note.textContent = msg || '';
      prods.forEach(function (p, i) {
        var v = firstAvail(p);
        var n = glassNameOf(p, null);
        chip(g, i, {
          label: n.name,
          fabLines: n.mesh ? [n.mesh] : [],
          sub: v ? '$' + v.price : '',
          was: v && parseFloat(v.compare_at_price || 0) > parseFloat(v.price || 0) ? '$' + v.compare_at_price : null,
          img: prodImgSrc(p),
          href: '/products/' + p.handle,
          jdgmHandle: p.handle,
          cart: v ? { id: v.id, available: !!v.available } : null,
          /* opening a product leaves search mode — the PDP wall wants the
             whole screen; back returns to the cached results */
          onOpen: function () { searchChrome(false); go(function () { renderProduct(p, prods, null); }); }
        });
      });
      /* the same label-shrink and row-evening every wall gets —
         paintGrid already ran; these chips arrived after it */
      Array.prototype.slice.call(g.querySelectorAll('.c3d-ovl__chiplabel')).forEach(function (el) {
        var size = 15;
        el.style.fontSize = '';
        while (size > 9 && el.scrollHeight > 60) {
          size -= 1;
          el.style.fontSize = size + 'px';
        }
      });
      if (g.__evenPass) { setTimeout(g.__evenPass, 60); setTimeout(g.__evenPass, 900); }
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
        try {
          var slots = Array.prototype.slice.call(
            g.querySelectorAll('.c3d-ovl__stars[data-product-handle]'));
          if (slots.length) {
            window.jdgm.batchRenderBadges(slots.map(function (el) {
              return { productHandle: el.getAttribute('data-product-handle'), badgePlaceholder: el };
            }));
          }
        } catch (e) {}
      }
    }
    function searchPaint() {
      paintGrid(function (g) {
        var note = document.createElement('p');
        note.className = 'c3d-ovl__searchnote';
        note.textContent = 'Type to search hats';
        g.appendChild(note);
        searchPaint._grid = g;
        searchPaint._note = note;
        if (searchLast) paintSearchResults(searchLast.prods, searchLast.msg);
      }, true, 'Search');
      searchChrome(true);
    }
    function runGlassSearch(q) {
      var seq = ++searchSeq;
      q = (q || '').trim();
      if (!q) { searchLast = null; paintSearchResults([], 'Type to search hats'); return; }
      paintSearchResults((searchLast && searchLast.prods) || [], 'Searching…');
      searchFetch(q).then(function (d) {
        if (seq !== searchSeq) return;
        var prods = (d && d.products) || [];
        var msg = !prods.length
          ? 'No hats matched “' + q + '” — try another word.'
          : (d.pages > 1
            ? d.count + ' matches — showing the first ' + prods.length
            : d.count + (d.count === 1 ? ' match' : ' matches'));
        searchLast = { q: q, prods: prods, msg: msg };
        paintSearchResults(prods, msg);
      }).catch(function () {
        if (seq === searchSeq) paintSearchResults([], 'Search hit a snag — try again.');
      });
    }
    function glassSearchLive() {
      /* the note is the liveness token: paintGrid wipes the grid's
         children when any other wall paints, disconnecting it */
      var ov = document.querySelector('.c3d-ovl.is-open.c3d-ovl--search');
      return !!(ov && searchPaint._note && searchPaint._note.isConnected);
    }
    function glassSearchOpen(inp) {
      searchBar = inp;
      if (glassSearchLive()) return;
      ovStack = [];
      go(searchPaint);
    }
    function renderSubsections(cfg, prods, title) {
      var subs = cfg.groups;
      var groups = {}, order = [];
      subs.forEach(function (s) { order.push(s.label); });
      if (!cfg.noMore) order.push('More');
      prods.forEach(function (p) {
        var bucket = null;
        for (var i = 0; i < subs.length; i++) {
          if (subs[i].match.test(p.title)) { bucket = subs[i].label; break; }
        }
        if (!bucket) {
          if (cfg.noMore) return;          /* only the named groups show */
          bucket = 'More';
        }
        (groups[bucket] = groups[bucket] || []).push(p);
      });
      var byLabel = {};
      subs.forEach(function (s) { byLabel[s.label] = s; });
      paintGrid(function (g) {
        var i = 0;
        order.forEach(function (s) {
          if (!groups[s]) return;
          var ps = groups[s];
          var def = byLabel[s];
          chip(g, i++, {
            label: s,
            sub: ps.length === 1 ? '1 hat' : ps.length + ' hats',
            img: prodImgSrc(ps[0]),
            onPick: (def && def.drill)
              ? function () { go(function () { renderSubsections(def.drill, ps, s); }); }
              : function () { go(function () { renderProducts(ps, null, s); }); }
          });
        });
      }, null, title || '');
    }
    function levelFor(handle, prods, title) {
      var t = title || handle.replace(/-/g, ' ');
      if (SUBSECTIONS[handle]) return function () { renderSubsections(SUBSECTIONS[handle], prods, t); };
      if (DIRECT_TILES[handle]) return function () { renderProducts(prods, null, t); };
      return function () { renderStyles(prods, t); };
    }
    /* the glass PDP photo sequence (Adam, 2026-08-17): the 3-hat composite
       first, the PONYTAIL-HOLE shot second when the hat has one (the real
       filename convention, learned from Florida ponytail pink — e.g.
       "...pink-white-ponytail-hole.png"; some ponytail hats have it, some
       don't yet), then the front shot, then the underbill. A WHITELIST,
       and lifestyle shots ("-woman-in-hat") are excluded everywhere — no
       person photos until Adam reshoots them. Glass only. */
    function glassGallery(p) {
      var imgs = p.images || [];
      function find(re) {
        for (var i = 0; i < imgs.length; i++) {
          var s = imgs[i].src, l = s.toLowerCase();
          if (l.indexOf('featureicon') >= 0) continue;
          if (l.indexOf('-in-hat') >= 0 || l.indexOf('lifestyle') >= 0) continue;
          if (re.test(l)) return s;
        }
        return null;
      }
      /* the FIRST shot must be the product's first real picture even when
         nothing is named 'main' (Adam, launch night 2026-08-27: "it should
         always open on the first picture some are opening on the 2nd or
         3rd" — those products had no main composite, so the back or front
         shot led). Fall back to the first non-excluded gallery image. */
      var first = find(/main/);
      if (!first) {
        for (var fi = 0; fi < imgs.length; fi++) {
          var fl = imgs[fi].src.toLowerCase();
          if (fl.indexOf('featureicon') >= 0 || fl.indexOf('-in-hat') >= 0 || fl.indexOf('lifestyle') >= 0) continue;
          first = imgs[fi].src;
          break;
        }
      }
      var seq = [first, find(/ponytail-hole|-back\./), find(/-front\./), find(/underbill/)];
      var out = [], seen = {};
      seq.forEach(function (s) { if (s && !seen[s]) { seen[s] = 1; out.push(s); } });
      return out.length ? out : [prodImgSrc(p)];
    }

    /* THE GLASS PRODUCT PAGE (Adam, 2026-08-17: "a glass product page with
       all of the swatches so they are never navigated away"): big image,
       name, stars, price, add to cart — and a swatch row of every sibling
       colorway in the current list. Tapping a swatch swaps the page in
       place (the stack top is REPLACED, so BACK always returns to the
       list, not back through every colour viewed). */
    /* ANALYTICS FROM INSIDE THE GLASS (Adam, 2026-08-17: "all of that
       needs to function inside of the glass"). A glass product open is
       a real product view, so the two systems that care are told:
       · Klaviyo — 'Viewed Product' + trackViewedItem, which is exactly
         what arms the browse-abandonment flow ("you were looking at…")
       · Shopify — a trekkie pageview + Viewed Product so sessions and
         product-view counts include glass shopping.
       SEO itself needs neither: crawlers read the real pages, and every
       glass tile carries a real href for them to follow. Swatch hops
       re-fire on purpose — each is a view of a different product. */
    function glassTrackProduct(p) {
      var url = window.location.origin + '/products/' + p.handle;
      var v = p.variants && p.variants[0];
      var price = v ? parseFloat(v.price) : null;
      var img = p.images && p.images[0] && p.images[0].src;
      try {
        var kl = window.klaviyo || window._learnq;
        if (kl && kl.push) {
          kl.push(['track', 'Viewed Product', {
            ProductName: p.title, ProductID: p.id, Handle: p.handle,
            ImageURL: img, URL: url, Brand: 'The Heartbeat Brand', Price: price
          }]);
          kl.push(['trackViewedItem', {
            Title: p.title, ItemId: p.id, ImageUrl: img, Url: url,
            Metadata: { Price: price }
          }]);
        }
      } catch (e) {}
      try {
        var sa = window.ShopifyAnalytics;
        if (sa && sa.lib) {
          if (sa.lib.page) sa.lib.page('/products/' + p.handle,
            { pageType: 'product', resourceType: 'product', resourceId: p.id });
          if (sa.lib.track) sa.lib.track('Viewed Product', {
            currency: (sa.meta && sa.meta.currency) || 'USD',
            variantId: v && v.id, productId: p.id,
            name: p.title, price: price, brand: 'The Heartbeat Brand'
          });
        }
      } catch (e) {}
    }
    /* THE SPEC CARD (Adam, 2026-08-17: "i really only want the section
       between unisex and color"): the description's SIZE / FIT / VISOR /
       MATERIAL / CLOSURE lines, parsed out of the body HTML. UNISEX and
       COLOR themselves stay out — the color is the hat on screen. */
    function specRows(p) {
      var html = p.body_html || p.description || '';
      if (!html) return [];
      var div = document.createElement('div');
      div.innerHTML = html;
      var rows = [], started = false;
      Array.prototype.slice.call(div.querySelectorAll('p, li')).forEach(function (el) {
        var line = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!line) return;
        if (/^unisex\b/i.test(line)) { started = true; return; }
        if (/^colou?r\s*:/i.test(line)) { started = false; return; }
        if (!started) return;
        var m2 = /^([a-z ]{2,20}):\s*(.+)$/i.exec(line);
        if (m2) rows.push({ k: m2[1].trim(), v: m2[2].trim() });
      });
      return rows;
    }
    /* how a collection's hats BREAK UP for the PDP's mini carousels:
       subsection collections (color vibes types, flag, straw) use their
       own groups; everything else groups by style in Adam's order. */
    function pdpGroups(handle, prods, subject) {
      /* STICKERS never ride the hat swatches (Adam, 2026-08-17: "you
         don't need more for the sticker") — a freedom sticker was the
         whole More mini on the freedom PDP */
      prods = prods.filter(function (q) { return !/sticker/i.test(q.title); });
      /* YOUTH NEVER TOUCHES ADULT (Adam, 2026-08-27): swatches stay in the
         subject's own age segment, in every collection */
      if (subject) {
        /* the marker is the TITLE PREFIX — "**YOUTH** …" (Adam, 2026-08-27) */
        var yRe = /^\s*\**\s*youth/i;
        var subjY = yRe.test(subject.title || '');
        prods = prods.filter(function (q) { return yRe.test(q.title || '') === subjY; });
      }
      var spec = SUBSECTIONS[handle];
      var groups = {}, order = [];
      if (spec) {
        prods.forEach(function (q) {
          for (var i = 0; i < spec.groups.length; i++) {
            if (spec.groups[i].match.test(q.title)) {
              (groups[spec.groups[i].label] = groups[spec.groups[i].label] || []).push(q);
              return;
            }
          }
          if (!spec.noMore) (groups['More'] = groups['More'] || []).push(q);
        });
        spec.groups.forEach(function (sg) { if (groups[sg.label]) order.push(sg.label); });
        if (groups['More']) order.push('More');
      } else {
        prods.forEach(function (q) {
          var s = styleOf(q);
          (groups[s] = groups[s] || []).push(q);
        });
        STYLE_ORDER.concat(['More']).forEach(function (L) { if (groups[L]) order.push(L); });
      }
      return { groups: groups, order: order };
    }
    function renderProduct(p, prods, styleName) {
      glassTrackProduct(p);
      glassUrlEnter(p);
      paintGrid(function (g) {
        var v = firstAvail(p);
        var wrap = document.createElement('div');
        wrap.className = 'c3d-ovl__pdp';
        /* rope products (Adam, 2026-08-28): square swatches so the wide
           rope-arc pictures fit instead of being cropped by the circle */
        if (/rope/i.test(p.title || '')) wrap.classList.add('c3d-ovl__pdp--ropes');
        /* claude- (2026-08-28, Adam): TWO COLUMNS on desktop — the buy
           block (photo/title/price/cart) on the RIGHT, the full swatch
           wall with the spec card on the LEFT. Both wrappers are
           display:contents below 1024px, so the mobile single-column
           flow is untouched. */
        var colBuy = document.createElement('div');
        colBuy.className = 'c3d-ovl__pdp-col c3d-ovl__pdp-col--buy';
        var colPick = document.createElement('div');
        colPick.className = 'c3d-ovl__pdp-col c3d-ovl__pdp-col--pick';
        wrap.appendChild(colBuy);
        wrap.appendChild(colPick);
        /* swipeable photo strip in Adam's order, with dots */
        var shots = glassGallery(p);
        var gal = document.createElement('div');
        gal.className = 'c3d-ovl__pdp-gal';
        var track = document.createElement('div');
        track.className = 'c3d-ovl__pdp-track';
        shots.forEach(function (src, si) {
          var im = document.createElement('img');
          im.className = 'c3d-ovl__pdp-img';
          im.crossOrigin = 'anonymous';
          im.src = src; im.alt = ''; im.decoding = 'async';
          /* the FIRST photo loads eagerly — the rest lazy. With all lazy,
             a later photo already in cache (the cart's add-on tiles prime
             them) painted before photo one, and scroll anchoring + snap
             settled the strip on it (Adam, launch night: "mostly from the
             cart open on the 2nd or 3rd picture"). */
          im.loading = si === 0 ? 'eager' : 'lazy';
          if (si === 0) { try { im.fetchPriority = 'high' } catch (e) {} }
          deWhite(im);
          track.appendChild(im);
        });
        /* RING (Adam, 2026-09-11: "product photos in the glass product
           should loop around instead of ending and having to go back") —
           the card strips' trick: a copy of the last photo before the
           first and a copy of the first after the last, the strip parks
           on the real first, and at rest on a copy it teleports to the
           copy's twin (identical pixels, invisible). Copies are built like
           the originals, not cloned, so deWhite runs on them too. */
        var ring = shots.length > 1;
        function ringImg (src) {
          var im = document.createElement('img');
          im.className = 'c3d-ovl__pdp-img';
          im.crossOrigin = 'anonymous';
          im.src = src; im.alt = ''; im.decoding = 'async'; im.loading = 'lazy';
          im.setAttribute('aria-hidden', 'true');
          deWhite(im);
          return im;
        }
        if (ring) {
          track.insertBefore(ringImg(shots[shots.length - 1]), track.firstChild);
          track.appendChild(ringImg(shots[0]));
        }
        function homeX () { return ring ? (track.clientWidth || 0) : 0 }
        gal.appendChild(track);
        /* ...and the strip is PINNED to photo one until the shopper
           touches it: every image load (deWhite re-fires load when it
           swaps in the cleaned copy) can shift a snap container, so each
           one re-asserts scrollLeft 0 until a real interaction. */
        var pinned = true;
        function unpin () { pinned = false }
        track.addEventListener('touchstart', unpin, { passive: true, once: true });
        track.addEventListener('wheel', unpin, { passive: true, once: true });
        track.addEventListener('pointerdown', unpin, { passive: true, once: true });
        function pinHome () { if (pinned && track.scrollLeft !== homeX()) track.scrollLeft = homeX() }
        Array.prototype.forEach.call(track.children, function (im) {
          im.addEventListener('load', pinHome);
        });
        requestAnimationFrame(pinHome);
        setTimeout(pinHome, 200);
        setTimeout(pinHome, 600);
        /* claude- (2026-08-27, Adam): the product's theme, top left above
           the hat picture — glass popup and PDP only, never the browsing
           cards. Re-renders with every swatch swap. */
        var corner = document.createElement('span');
        corner.className = 'c3d-theme-corner';
        corner.textContent = (p.title || '').split(' - ')[0].replace(/\s*Hat\s*$/i, '').toUpperCase();
        if (corner.textContent) colBuy.appendChild(corner);
        var dots = null;
        if (shots.length > 1) {
          dots = document.createElement('div');
          dots.className = 'c3d-ovl__pdp-dots';
          shots.forEach(function (_, di) {
            var d = document.createElement('span');
            if (di === 0) d.className = 'is-on';
            dots.appendChild(d);
          });
          gal.appendChild(dots);
          var ringN = shots.length, ringSettle = null;
          track.addEventListener('scroll', function () {
            var w = track.clientWidth || 1, idx = Math.round(track.scrollLeft / w);
            var k = ring ? ((idx - 1) % ringN + ringN) % ringN : idx;   /* copy-aware -> real photo */
            Array.prototype.forEach.call(dots.children, function (d, di) {
              d.className = di === k ? 'is-on' : '';
            });
            if (ring) {
              clearTimeout(ringSettle);
              ringSettle = setTimeout(function () {
                /* at rest on a copy -> jump to its twin; never mid-gesture
                   or mid-glide (the follower owns scrollLeft until it rests) */
                if (track.__c3dDrag || track.__c3dRaf) return;
                var w2 = track.clientWidth || 1, i2 = Math.round(track.scrollLeft / w2);
                if (i2 === 0) track.scrollLeft = ringN * w2;
                else if (i2 === ringN + 1) track.scrollLeft = w2;
              }, 140);
            }
          }, { passive: true });
        }
        colBuy.appendChild(gal);
        var n = glassNameOf(p, styleName);
        var ttl = document.createElement('p');
        ttl.className = 'c3d-ovl__chiplabel c3d-ovl__pdp-title';
        ttl.textContent = n.name;
        colBuy.appendChild(ttl);
        if (n.mesh) {
          var fl = document.createElement('span');
          fl.className = 'c3d-ovl__fabline';
          fl.textContent = n.mesh;
          colBuy.appendChild(fl);
        }
        var stars = document.createElement('div');
        stars.className = 'c3d-ovl__stars';
        stars.setAttribute('data-product-handle', p.handle);
        colBuy.appendChild(stars);
        var pr = document.createElement('p');
        pr.className = 'c3d-ovl__chipsub c3d-ovl__pdp-price';
        /* the compare-at is struck beside the price (Adam, launch night:
           "the glass does not show the compare at ... 34.99 swiped
           through then 29.99"). Cents ride as data attrs so the pricing
           engine never parses the struck text. */
        if (v) {
          var prCmp = parseFloat(v.compare_at_price || 0);
          var prPrc = parseFloat(v.price || 0);
          pr.dataset.ppcents = String(Math.round(prPrc * 100));
          if (prCmp > prPrc) {
            pr.dataset.ppcompare = String(Math.round(prCmp * 100));
            pr.innerHTML = '<s class="c3d-pp-was">$' + v.compare_at_price + '</s> $' + v.price;
          } else {
            pr.textContent = '$' + v.price;
          }
        } else {
          pr.textContent = '';
        }
        colBuy.appendChild(pr);
        colBuy.appendChild(mixEl());
        if (v && !v.available && klaviyoCompanyId()) {
          /* sold out on the glass PDP: quiet flag + NOTIFY ME, no dead
             button — email captured right here, never leaving the glass */
          var sf = document.createElement('span');
          sf.className = 'c3d-ovl__soldflag';
          sf.textContent = 'Sold out';
          colBuy.appendChild(sf);
          colBuy.appendChild(notifyControl(v.id));
        } else {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c3d-ovl__atc c3d-ovl__pdp-atc';
        if (v && v.available) { btn.textContent = 'Add to cart'; }
        else {
          btn.textContent = 'Sold out';
          btn.disabled = true;
        }
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          cartAdd(v.id).then(function () {
            btn.textContent = 'Added ✓';
            btn.disabled = false;
            /* rope popup retired (2026-08-26) — see cart Suggested Add-Ons */
          }).catch(function () {
            btn.textContent = 'Try again';
            btn.disabled = false;
          });
        });
        colBuy.appendChild(btn);
        }
        /* VIEW FULL DETAILS — under Add to cart, ABOVE the swatches (Adam,
           2026-08-25). data-c3d-native so the router NAVIGATES to the full
           glass product page instead of re-opening this quick-view. */
        var full = document.createElement('a');
        full.className = 'c3d-ovl__fulllink';
        full.setAttribute('data-c3d-native', '');
        full.href = '/products/' + p.handle;
        full.textContent = 'View full details';
        colBuy.appendChild(full);
        /* every sibling colourway as a swatch — and on color vibes ALL of
           them visible at once (Adam, 2026-08-27: "i want the swatches to
           all be seen right there cause there are so many"): the noCross
           collections wrap into a wall instead of a scroll strip */
        var swWall = lastCol && SUBSECTIONS[lastCol.handle] && SUBSECTIONS[lastCol.handle].noCross;
        var sw = document.createElement('div');
        sw.className = 'c3d-ovl__swatches' + (swWall ? ' c3d-ovl__swatches--wall' : '');
        prods.forEach(function (sib) {
          var s = document.createElement('button');
          s.type = 'button';
          s.className = 'c3d-ovl__swatch' + (sib === p ? ' is-current' : '');
          var si = document.createElement('img');
          si.src = prodImgSrc(sib); si.alt = colorwayOf(sib, styleName);
          si.loading = 'lazy'; si.decoding = 'async';
          s.appendChild(si);
          if (sib !== p) {
            s.addEventListener('click', function () {
              ovStack.pop();
              go(function () { renderProduct(sib, prods, styleName); });
            });
          }
          sw.appendChild(s);
        });
        colPick.appendChild(sw);
        /* OTHER GROUPS under the main swatches (Adam, 2026-08-17):
           half-width labelled mini swatch carousels, two per line. The
           grouping follows the COLLECTION'S OWN BREAKUP — styles for
           most, color TYPES for color vibes and the other subsection
           collections ("you can put the same rule for color vibes how
           it is broken up into type"). Picking from another group
           promotes that group to the main row. */
        /* noCross (color vibes, 2026-08-27): the "other group" is always
           the other AGE SEGMENT — never shown inside a product ("don't
           have youth swatches inside the adult product"). */
        var colSpec0 = lastCol && SUBSECTIONS[lastCol.handle];
        if (lastCol && lastCol.prods && lastCol.prods.length && !(colSpec0 && colSpec0.noCross)) {
          var grpInfo = pdpGroups(lastCol.handle, lastCol.prods, p);
          var curLabel = null;
          grpInfo.order.forEach(function (L) {
            if (!curLabel && grpInfo.groups[L].some(function (q) { return q.handle === p.handle; })) curLabel = L;
          });
          if (!curLabel) curLabel = styleName || styleOf(p);
          var others = document.createElement('div');
          others.className = 'c3d-ovl__ostyles';
          grpInfo.order.forEach(function (L) {
            if (L === curLabel || !grpInfo.groups[L] || !grpInfo.groups[L].length) return;
            var box = document.createElement('div');
            box.className = 'c3d-ovl__ostyle';
            var oh = document.createElement('p');
            oh.className = 'c3d-ovl__ostylehead';
            oh.textContent = L.toUpperCase();
            box.appendChild(oh);
            var row = document.createElement('div');
            row.className = 'c3d-ovl__swatches c3d-ovl__swatches--mini';
            var Lstyle = STYLE_ORDER.indexOf(L) >= 0 ? L : null;
            grpInfo.groups[L].forEach(function (q) {
              var ob = document.createElement('button');
              ob.type = 'button';
              ob.className = 'c3d-ovl__swatch';
              var oi = document.createElement('img');
              oi.src = prodImgSrc(q); oi.alt = colorwayOf(q, Lstyle);
              oi.loading = 'lazy'; oi.decoding = 'async';
              ob.appendChild(oi);
              ob.addEventListener('click', function () {
                ovStack.pop();
                go(function () { renderProduct(q, grpInfo.groups[L], Lstyle); });
              });
              row.appendChild(ob);
            });
            box.appendChild(row);
            others.appendChild(box);
          });
          if (others.children.length) colPick.appendChild(others);
        }
        /* edge fades on every swatch strip, main and minis */
        Array.prototype.slice.call(wrap.querySelectorAll('.c3d-ovl__swatches')).forEach(edgeFade);
        /* the spec card, under all the swatches */
        var specs = specRows(p);
        if (specs.length) {
          var specGrid = document.createElement('div');
          specGrid.className = 'c3d-ovl__specs';
          specs.forEach(function (srow) {
            var cell = document.createElement('div');
            cell.className = 'c3d-ovl__spec' + (srow.v.length > 38 ? ' c3d-ovl__spec--wide' : '');
            var kEl = document.createElement('span');
            kEl.className = 'c3d-ovl__speck';
            kEl.textContent = srow.k.toUpperCase();
            var vEl = document.createElement('span');
            vEl.className = 'c3d-ovl__specv';
            vEl.textContent = srow.v;
            cell.appendChild(kEl);
            cell.appendChild(vEl);
            specGrid.appendChild(cell);
          });
          colPick.appendChild(specGrid);
        }
        g.appendChild(wrap);
      }, 'pdp');
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
        try {
          var slot = document.querySelector('.c3d-ovl .c3d-ovl__pdp .c3d-ovl__stars');
          if (slot) window.jdgm.batchRenderBadges([{ productHandle: p.handle, badgePlaceholder: slot }]);
        } catch (e) {}
      }
    }

    function renderStyles(prods, title) {
      var groups = {};
      prods.forEach(function (p) {
        var s = styleOf(p);
        (groups[s] = groups[s] || []).push(p);
      });
      var order = STYLE_ORDER.concat(['More']);
      paintGrid(function (g) {
        var i = 0;
        order.forEach(function (s) {
          if (!groups[s]) return;
          var ps = groups[s];
          chip(g, i++, {
            label: s,
            sub: ps.length === 1 ? '1 hat' : ps.length + ' hats',
            img: prodImgSrc(ps[0]),
            onPick: function () { go(function () { renderProducts(ps, s); }); }
          });
        });
      }, null, title || '');
    }
    function collectionTile(g, i, l, wordOnly) {
      var el = chip(g, i, {
        label: l.label,
        img: l.img,
        href: l.href,
        onPick: function () {
          /* claude- (2026-08-24, Adam): a collection tile takes you TO the
             collection page — the in-glass product wall is retired. Pages
             still open in the glass reader via leafPick. */
          if (/\/collections\//.test(l.href)) { window.location.href = l.href; return; }
          leafPick(l.href);
        }
      });
      if (wordOnly) el.classList.add('c3d-ovl__chip--txt');
      else lazyChipImg(el, l.href);
      return el;
    }
    function renderCollections(links, title) {
      paintGrid(function (g) {
        links.forEach(function (l, i) { collectionTile(g, i, l); });
      }, null, title || '');
    }
    /* THE ALL HATS DIRECTORY (Adam, 2026-08-17: "inside all hats i want
       sub categories... states in copperplate and the states a-z
       underneath and so on; if it doesn't have a category put it under a
       More heading, keeping the a-z format"): Copperplate group headers
       starting at the left — States, Sports, Passions — each with its
       collections A–Z beneath, then More with every remaining collection
       from the nav, also A–Z. */
    function renderDirectory(items) {
      var groups = [], grouped = {};
      ['STATES', 'SPORTS', 'PASSIONS'].forEach(function (gl) {
        var it = null;
        items.forEach(function (x) {
          if (!it && x.children && x.label.toUpperCase() === gl) it = x;
        });
        if (!it) return;
        var kids = it.children.filter(function (k) {
          return /\/collections\//.test(k.href || '');
        }).slice().sort(function (a, b) { return a.label.localeCompare(b.label); });
        kids.forEach(function (k) { grouped[k.label.toUpperCase()] = 1; });
        grouped[gl] = 1;   /* the group's own parent collection never
                              repeats itself inside More */
        if (kids.length) groups.push({ label: it.label, kids: kids });
      });
      var uni = {}, more = [];
      Array.prototype.slice.call(document.querySelectorAll(
        'header a[href*="/collections/"], .fixed-nav-top a[href*="/collections/"]'
      )).forEach(function (a) {
        var label = normMenuLabel(a.textContent.trim().replace(/\s+/g, ' '));
        var href = a.getAttribute('href');
        if (!label || !href) return;
        var u = label.toUpperCase();
        if (uni[u] || grouped[u]) return;
        if (/gift card/i.test(label)) return;   /* not a hat category */
        /* USA 250 and FREEDOM live inside the FLAG subsections — never
           repeated in More (Adam, 2026-08-17) */
        if (u === 'USA 250' || u === 'USA' || u === 'FREEDOM') return;
        uni[u] = 1;
        more.push({ label: label, href: href });
      });
      more.sort(function (a, b) { return a.label.localeCompare(b.label); });
      /* the directory is a LIST — the small word buttons, no pictures
         (Adam, 2026-08-17: "i like how you had them without the pictures
         and as a list in the smaller blocks") */
      paintGrid(function (g) {
        var i = 0;
        function header(text) {
          var h = document.createElement('p');
          h.className = 'c3d-ovl__ghead';
          h.textContent = dispLabel(text);
          g.appendChild(h);
        }
        groups.forEach(function (gr) {
          header(gr.label);
          gr.kids.forEach(function (k) { collectionTile(g, i++, k, true); });
        });
        if (more.length) {
          header('More');
          more.forEach(function (k) { collectionTile(g, i++, k, true); });
        }
      }, 'menu', 'All Hats');
    }
    /* tiles without a baked picture (states, sports, menu walls) fill in
       the collection's first best-seller composite as it loads — and the
       fetch doubles as the tap prefetch */
    /* some collections need a SPECIFIC hat fronting their tile — boonies'
       first product is the white camo whose photo is bad; Adam: "use the
       gray boonie". The beanie/straw walls front their TOP SELLER (Adam,
       2026-08-20: "use the top selling for the images") — resolved from
       Admin sortKey BEST_SELLING on 2026-08-20; re-pin when sales shift.
       Boonies keeps the gray: the top seller is that same bad-photo white
       camo, and the photo call outranks the general rule. */
    var COL_TILE_PICK = {
      'boonies': /gray camo/i,
      'beanies-hats': /tahoe beanie.*pom.*black/i,
      'tahoe-beanies': /tahoe beanie.*pom.*black/i,
      'mountains-beanies': /mountains beanie.*cuffed.*black/i,
      'florida-beanies': /ribbed cuff.*black/i,
      'idaho-beanies': /marled cuff.*black/i,
      'cuffed-beanies-hats': /mountains beanie.*cuffed.*black/i,
      'pom-beanies-hats': /tahoe beanie.*pom.*black/i,
      'no-cuff-beanies-hats': /no cuff.*charcoal/i,
      'straw-hats': /flag rugged/i,
      'tahoe-straw': /emerald bay sunrise/i,
      'florida-straw': /palms/i,
      'idaho-straw': /sawtooth/i,
      'nevada-straw': /mountains/i,
      'surf-straw': /wave/i,
      'flag-straw': /flag rugged/i
    };
    function lazyChipImg(el, href) {
      if (!el || el.querySelector('.c3d-ovl__chipimg')) return;
      var mm = /\/collections\/([^\/?#]+)/.exec(href || '');
      if (!mm) return;
      fetchCollectionLite(mm[1]).then(function (prods) {
        if (!prods.length || !el.parentNode) return;
        var pickP = prods[0];
        var re = COL_TILE_PICK[mm[1]];
        if (re) {
          for (var pi = 0; pi < prods.length; pi++) {
            if (re.test(prods[pi].title)) { pickP = prods[pi]; break; }
          }
        }
        /* claude- (2026-08-27, Adam: "camping needs a 3 hat photo",
           "team heros needs a 2 hat photo if we have it"): wall tiles wear
           the FAMILY '-main' shot, so a tile whose lead product is one of
           the 45 single-hat cutouts (cutFor outranks 'main' in prodImgSrc)
           no longer shows a lone hat — and a lead with no family shot at
           all (camping) borrows the first sibling that has one. Pinned
           picks (COL_TILE_PICK) keep their approved images untouched. */
        var src = null;
        if (!re) {
          for (var pi2 = 0; pi2 < prods.length && !src; pi2++) {
            var fimgs = prods[pi2].images || [];
            for (var fi = 0; fi < fimgs.length && !src; fi++) {
              var fs = fimgs[fi].src;
              if (imgOk(fs) && fs.toLowerCase().indexOf('main') >= 0) src = fs;
            }
          }
        }
        if (!src) src = prodImgSrc(pickP);
        if (!src || el.querySelector('.c3d-ovl__chipimg')) return;
        var im = document.createElement('img');
        im.className = 'c3d-ovl__chipimg';
        im.crossOrigin = 'anonymous';
        im.src = src; im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        deWhite(im);
        el.insertBefore(im, el.firstChild);
      }).catch(function () {});
    }

    /* THE WHOLE STORE IN THE GLASS (Adam, 2026-08-17: "I want all of the
       shopping to be done through the glass — any link, page, the
       swatches in the hero... even the exchange/return policy and privacy
       notices, our mission, ambassadors, contact, gift card, stickers —
       not blanks or custom yet"). A capture-phase click router on the
       homepage sends same-origin links into the glass: collections open
       the shop, products open the glass product page, pages and policies
       open a glass reader whose forms (contact) submit in place. Blanks,
       custom, wholesale, cart, checkout, account and anything external
       (Instagram cannot be embedded) stay untouched. */
    var GLASS_SKIP = /^\/(pages\/(blanks|custom|wholesale)|cart|checkout|account|challenge|apps)/;
    /* Adam's scrolling top bar (2026-08-17: "it all needs to open its
       pages in glass") — three of its links can't be answered by their
       own target: sports-hats is an EMPTY display collection (0
       products, so the glass used to give up and navigate), and States /
       Passions point at desktop landing pages (the passions one put a
       stray 2-for-$60 banner in the glass reader). Each opens the same
       glass wall the menu builds for that group instead. */
    var NAV_WALL_COL = { 'sports-hats': 'SPORTS' };
    var NAV_WALL_PAGE = {
      'states-and-cities': 'STATES',
      'dog-mountains-beach': 'PASSIONS',
      'passions': 'PASSIONS'
    };
    function openMenuWall(name) {
      var grp = null;
      readFullMenu().forEach(function (it) {
        if (!grp && it.children && it.label.toUpperCase() === name) grp = it;
      });
      if (!grp) return false;
      var colKids = grp.children.filter(function (k) { return /\/collections\//.test(k.href || ''); });
      go(colKids.length
        ? function () { renderCollections(grp.children, grp.label); }
        : function () { renderMenu(grp.children, grp.label); });
      return true;
    }
    function glassRoute(path) {
      /* one router for everything: collections → the shop, products → the
         glass PDP, pages/policies → the glass reader. Excluded paths and
         anything unrecognised fall back to real navigation. Returns true
         when the glass took it. */
      if (GLASS_SKIP.test(path)) return false;
      /* claude- (2026-08-24, Adam, corrected same day): PRODUCTS still open
         the glass PDP — "i didn't want the old product page at all". What was
         retired is the HOMEPAGE shop-from-glass (collection walls / subsection
         walls from the home cards) and the styles glass; collection links
         navigate to real collection pages. */
      var m = /\/products\/([^\/?#]+)/.exec(path);
      if (m) {
        var directP = fetch('/products/' + m[1] + '.js')
          .then(function (r) { if (!r.ok) throw new Error('p ' + r.status); return r.json(); })
          .then(function (pj) {
            return {
              id: pj.id, title: pj.title, handle: pj.handle,
              description: pj.description,   /* the spec card reads it */
              images: (pj.images || []).map(function (s) { return { src: typeof s === 'string' ? s : s.src }; }),
              variants: (pj.variants || []).map(function (v) {
                return { id: v.id, available: v.available, price: (v.price / 100).toFixed(2),
                  compare_at_price: v.compare_at_price ? (v.compare_at_price / 100).toFixed(2) : null,
                  title: v.title };
              })
            };
          });
        /* SWATCHES EVERYWHERE (Adam, 2026-08-17: "add in the swatches
           ... just like from the homepage, they should always be the
           same throughout"): a direct open used to know only its own
           product — one lonely swatch — where the homepage's walls pass
           the whole style family. When the page knows its collection
           (the collection template stamps data-c3d-collection), the
           family is fetched and the PDP gets the same full swatch row. */
        var colEl = document.querySelector('[data-c3d-collection]');
        var colH = colEl && colEl.getAttribute('data-c3d-collection');
        var sibsP = colH
          ? fetchCollection(colH).catch(function () { return []; })
          : Promise.resolve([]);
        Promise.all([directP, sibsP]).then(function (r) {
          var p = r[0], prods = r[1] || [];
          var mine = null;
          prods.forEach(function (q) { if (q.handle === p.handle) mine = q; });
          var subject = mine || p;
          /* siblings follow the collection's own breakup — style family
             for most, color TYPE for color vibes etc (same rule as the
             PDP minis) */
          var sibs = [subject], sName = null;
          if (mine && colH) {
            var gi = pdpGroups(colH, prods, p);
            gi.order.forEach(function (L) {
              if (sName === null && gi.groups[L].some(function (q) { return q.handle === p.handle; })) {
                sibs = gi.groups[L];
                sName = STYLE_ORDER.indexOf(L) >= 0 ? L : '';
              }
            });
            if (sName === '') sName = null;
          }
          go(function () { renderProduct(subject, sibs, sName); });
        }).catch(function () { window.location.href = path; });
        return true;
      }
      /* SHOP ALL HATS (hero, /collections/all) and the A-Z page both open
         the All Hats DIRECTORY (Adam, 2026-08-17: "it should go to the
         same menu that we created") */
      if (/^\/collections\/all\/?$/.test(path) || /^\/pages\/all-hats/.test(path)) {
        /* claude- (2026-08-28, Adam): HATS goes to the REAL Shop All Hats
           A-Z page — pictures, no carousel, no glass. The view param
           forces the template until it's assigned in admin (launch
           checklist); drop the param after assignment. */
        if (/view=claude-all-hats/.test(window.location.search)) return false;
        window.location.href = '/pages/all-hats-a-z?view=claude-all-hats';
        return true;
      }
      /* claude- (2026-08-27, Adam: "when i clock beanies it should open
         Mountains Beanie, Tahoe Beanie and Idaho beanie sections to pick
         from before entering into beanies, the customer will have a
         preference which logo") — a beanies click opens the three-family
         chooser wall; each tile enters its own beanie collection. */
      if (/^\/collections\/beanies(-hats)?\/?$/.test(path)) {
        go(function () {
          renderCollections([
            { label: 'Mountains Beanies', href: '/collections/mountains-beanies' },
            { label: 'Tahoe Beanies', href: '/collections/tahoe-beanies' },
            { label: 'Idaho Beanies', href: '/collections/idaho-beanies' }
          ], 'Beanies');
        });
        return true;
      }
      /* claude- (2026-08-27, Adam: "straw should get the same chooser
         treatment with the different logos") — six live straw families,
         tile images already pinned in COL_TILE_PICK. */
      if (/^\/collections\/straw-hats\/?$/.test(path)) {
        go(function () {
          renderCollections([
            { label: 'Flag Straw', href: '/collections/flag-straw' },
            { label: 'Tahoe Straw', href: '/collections/tahoe-straw' },
            { label: 'Florida Straw', href: '/collections/florida-straw' },
            { label: 'Idaho Straw', href: '/collections/idaho-straw' },
            { label: 'Nevada Straw', href: '/collections/nevada-straw' },
            { label: 'Surf Straw', href: '/collections/surf-straw' }
          ], 'Straw Hats');
        });
        return true;
      }
      m = /^\/collections\/([^\/?#]+)\/?$/.exec(path);
      if (m) {
        var h = m[1];
        /* a group link (Sports → empty display collection) still opens its
           glass LIST of collections; everything else navigates to the real
           collection page (Adam, 2026-08-24) */
        if (NAV_WALL_COL[h] && openMenuWall(NAV_WALL_COL[h])) return true;
        return false;
      }
      m = /^\/pages\/([^\/?#]+)/.exec(path);
      if (m && NAV_WALL_PAGE[m[1]] && openMenuWall(NAV_WALL_PAGE[m[1]])) return true;
      /* claude- (2026-08-27, Adam: "the about us click in the menus should
         now lead us to the page"): pages and policies NAVIGATE — every page
         carries the glass treatment now, so the glass reader pop-out is
         retired for them. The category walls (states/passions above) and
         the All Hats directory keep their glass. renderPage/bootHulk stay
         in the file for rollback. */
      return false;
    }
    window.__c3dGlassOpenCollection = function (href) {
      try {
        var path = new URL(href, window.location.origin).pathname;
        ovStack = [];
        return glassRoute(path);
      } catch (e) { return false; }
    };
    /* THE HULK FORM (returns & exchanges, Adam 2026-08-17: "returns and
       exchanges should be the hulk form that i built as glass"). The
       form is MOUNTED by the app's JS into an empty .pxFormGenerator
       div, so extraction alone shows a blank card — and Shopify sends
       frame-ancestors 'none' on every storefront page, so framing the
       real page is impossible. Instead the app's two scripts (the
       inline window.hulkFormBuilder config and the CDN renderer) are
       re-run HERE after the glass mounts the div: the form builds
       itself inside the glass and Hulk's own JS handles submission. */
    function bootHulk(doc) {
      var cfg = null, src = null;
      Array.prototype.slice.call(doc.querySelectorAll('script')).forEach(function (s) {
        var t = s.textContent || '';
        if (!s.getAttribute('src') && /hulkFormBuilder/.test(t) && !cfg) cfg = t;
        if (s.getAttribute('src') && /form-builder/i.test(s.getAttribute('src')) && !src) src = s.getAttribute('src');
      });
      if (!cfg || !src) return;
      Array.prototype.slice.call(document.querySelectorAll('.c3d-hulk-boot')).forEach(function (n) { n.remove(); });
      /* the app's loader only boots on DOMContentLoaded — long gone by
         now — so its exact chain is reproduced here: config, loader,
         the assets the loader's handler would fetch (styles, then
         searchableDropdown → extraFunctions), then the init calls the
         handler makes. Read out of the loader source 2026-08-17. */
      function el(tag, attrs) {
        var n = document.createElement(tag);
        n.className = 'c3d-hulk-boot';
        for (var k in attrs) n[k] = attrs[k];
        return n;
      }
      function js(u, then) {
        if (!u) { then(); return; }
        var s = el('script', { src: u });
        s.onload = then;
        s.onerror = then;
        document.body.appendChild(s);
      }
      var c = el('script', {});
      c.textContent = cfg;
      document.body.appendChild(c);
      js(src, function () {
        var a = (window.hulkFormBuilder && window.hulkFormBuilder.assets) || {};
        ['extraStyles', 'bootstrapStyles', 'formLayoutStyles'].forEach(function (k) {
          if (a[k]) document.head.appendChild(el('link', { rel: 'stylesheet', href: a[k] }));
        });
        js(a.searchableDropdown, function () {
          js(a.extraFunctions, function () {
            if (typeof window.GetCorePage === 'function') window.GetCorePage();
            if (typeof window.hulkFBInitFormBuilder === 'function') window.hulkFBInitFormBuilder();
            if (typeof window.hulkFBKeyboardAccess === 'function') window.hulkFBKeyboardAccess();
          });
        });
      });
    }
    function renderPage(href, title) {
      paintGrid(function (g) {
        var card = document.createElement('div');
        card.className = 'c3d-ovl__page';
        card.textContent = 'Loading…';
        g.appendChild(card);
        fetch(href).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          /* pages with a FORM (contact) take the whole main content — the
             theme keeps the form in its own section, outside .rte, and
             the narrow pick rendered contact with no form at all
             (Adam, 2026-08-17: "the contact form isn't working") */
          var node = doc.querySelector('.shopify-policy__container')
            || (doc.querySelector('main form') ? doc.querySelector('main') : null)
            || doc.querySelector('main .rte')
            || doc.querySelector('main')
            || doc.body;
          Array.prototype.slice.call(node.querySelectorAll('script, style, iframe, noscript')).forEach(function (n) { n.remove(); });
          card.textContent = '';
          var body = document.createElement('div');
          body.className = 'c3d-ovl__pagebody';
          body.innerHTML = node.innerHTML;
          card.appendChild(body);
          /* an app-mounted form (Hulk) needs its scripts re-run now
             that its mount div lives inside the glass */
          if (body.querySelector('.pxFormGenerator')) bootHulk(doc);
          /* forms (contact) submit from inside the glass */
          Array.prototype.slice.call(body.querySelectorAll('form')).forEach(function (f) {
            f.addEventListener('submit', function (e) {
              e.preventDefault();
              var sb = f.querySelector('[type="submit"]');
              if (sb) sb.disabled = true;
              fetch(f.getAttribute('action') || href, { method: 'POST', body: new FormData(f) })
                .then(function (r) {
                  /* Shopify may bounce a programmatic post to its spam
                     challenge — that needs a human on the REAL page, so
                     hand over honestly instead of faking a success */
                  if (r.url && r.url.indexOf('/challenge') >= 0) {
                    window.location.href = href;
                    return;
                  }
                  if (!r.ok) throw new Error('form ' + r.status);
                  f.innerHTML = '';
                  f.textContent = 'Sent ✓ — we will get back to you.';
                })
                .catch(function () { if (sb) sb.disabled = false; });
            });
          });
        }).catch(function () { window.location.href = href; });
      }, 'pdp');
    }
    /* THE GLASS MENU (Adam, 2026-08-17: "i even want the menu popout to
       be glass"): the hamburger opens a glass wall of the store's menu —
       groups drill in place, leaves route through glassRoute. The model
       is read live off the theme's own menu drawer DOM. */
    function findMenuRoot() {
      var s = Array.prototype.slice.call(document.querySelectorAll('details summary')).filter(function (x) {
        return /passions|sports|states/i.test(x.textContent);
      })[0];
      if (s) {
        var host = s.closest('nav') || s.closest('[class*="drawer" i]') || s.closest('[class*="menu" i]');
        if (host) return host;
      }
      return document.querySelector('header nav') || document.querySelector('header') || document.body;
    }
    function readMenu(root) {
      var items = [], seen = {};
      Array.prototype.slice.call(root.querySelectorAll('details')).forEach(function (d) {
        var s = d.querySelector('summary');
        if (!s) return;
        var label = s.textContent.trim().replace(/\s+/g, ' ');
        var kseen = {};
        var kids = Array.prototype.slice.call(d.querySelectorAll('a')).map(function (a) {
          return { label: a.textContent.trim().replace(/\s+/g, ' '), href: a.getAttribute('href') };
        }).filter(function (k) {
          if (!k.label || !k.href) return false;
          var u = k.label.toUpperCase();
          if (u.indexOf('7K') >= 0 || String(k.href).indexOf('7k-club') >= 0) return false;   /* unlisted (2026-08-27) */
          if (u === label.toUpperCase()) return false;   /* no self-link row */
          if (kseen[u]) return false;
          kseen[u] = 1;
          return true;
        });
        if (label && kids.length && !seen[label.toUpperCase()]) {
          seen[label.toUpperCase()] = 1;
          items.push({ label: label, children: kids });
        }
      });
      Array.prototype.slice.call(root.querySelectorAll('a')).forEach(function (a) {
        if (a.closest('details')) return;
        var label = a.textContent.trim().replace(/\s+/g, ' ');
        if (/^hats$/i.test(label)) label = 'All Hats';   /* Adam, 2026-08-17 */
        var href = a.getAttribute('href');
        if (!label || !href || href.charAt(0) === '#') return;
        if (/log ?in|basket|cart|search/i.test(label)) return;
        if (seen[label.toUpperCase()]) return;
        seen[label.toUpperCase()] = 1;
        items.push({ label: label, href: href });
      });
      return items;
    }
    /* the FULL menu (Adam, 2026-08-17: "i didn't say take anything out")
       — union of the drawer nav AND the scrolling top bar, with a group
       literally named MORE flattened into the wall so Our Mission,
       Pickleball Paddles, Stickers, Gift Card etc all show. Wholesale,
       Custom and Blanks are listed too — they route natively per the
       standing exclusion. */
    function normMenuLabel(label) {
      return /^hats$/i.test(label) ? 'All Hats' : label;
    }
    function readFullMenu() {
      var base = readMenu(findMenuRoot());
      var seen = {}, childSeen = {};
      var items = [];
      base.forEach(function (it) {
        it.label = normMenuLabel(it.label);
        if (it.children) {
          it.children.forEach(function (k) { childSeen[k.label.toUpperCase()] = 1; });
        }
      });
      base.forEach(function (it) {
        if (it.children && /^more$/i.test(it.label)) {
          it.children.forEach(function (k) {
            var u = k.label.toUpperCase();
            if (seen[u]) return;
            seen[u] = 1;
            items.push(k);
          });
          return;
        }
        var u = it.label.toUpperCase();
        if (seen[u]) return;
        seen[u] = 1;
        items.push(it);
      });
      /* the scrolling TOP BAR is Adam's own curated top level (USA 250,
         Freedom, Color Vibes, Hat Ropes, Wholesale, Custom, Blanks…) —
         everything on it joins the wall even when it also lives inside a
         group. Other header links join only when they are NOT already a
         child of a group (keeps fifty states off the top level). */
      function sweep(selector, respectChildren) {
        Array.prototype.slice.call(document.querySelectorAll(selector)).forEach(function (a) {
          var label = normMenuLabel(a.textContent.trim().replace(/\s+/g, ' '));
          var href = a.getAttribute('href');
          if (!label || !href || href.charAt(0) === '#') return;
          if (/^(home|more)$/i.test(label)) return;
          if (/log ?in|basket|cart|search/i.test(label)) return;
          var u = label.toUpperCase();
          if (seen[u]) return;
          if (respectChildren && childSeen[u]) return;
          seen[u] = 1;
          items.push({ label: label, href: href });
        });
      }
      sweep('.fixed-nav-top a', false);
      sweep('header nav a', true);
      /* BEANIES / STRAW / BOONIES — glass-only additions (Adam, 2026-08-20:
         "add in beanies straw and boonies after sale with pictures ...
         beanies will have the categories such as mountains and tahoe to
         click into, straw will have the same thing. just in the glass, not
         update the menu entirely"). Synthetic because the real nav must not
         change; GLASS_MENU_ORDER seats them after SALE!. Groups drill into
         the same collection wall every other group uses. */
      [
        /* Beanies re-cut 2026-08-20 per Adam: Florida OUT ("there is
           nothing there"); Cuffed/Pom/No Cuff live INSIDE each place, not
           at the top; Idaho has no No Cuff products so it shows two. Style
           tiles open PRODUCT walls filtered by title — no per-place style
           collections exist and none are needed. Tile art = white
           backgrounds cut out ("if they have a white background on
           anything you can cut them out") — claude-bn-*.png assets via
           data-beanie-art. */
        { label: 'Beanies', href: '/collections/beanies-hats', children: [
          /* no top-level art: the tile wears MENU_TILE_IMG's white pom
             (Adam, 2026-08-27); the drill tiles keep their cut art */
          /* claude- (2026-08-24, Adam): the styles glass is retired — every
             leaf here NAVIGATES to its real collection page. The style level
             (Cuffed/Pom/No Cuff product walls) is gone with it. */
          { label: 'All Beanies', href: '/collections/beanies-hats', art: 'tahoe-pom' },
          { label: 'Tahoe', href: '/collections/tahoe-beanies', art: 'tahoe-pom' },
          { label: 'Mountains', href: '/collections/mountains-beanies', art: 'mtn-cuffed' },
          { label: 'Idaho', href: '/collections/idaho-beanies', art: 'idaho-cuffed' }
        ] },
        { label: 'Straw', href: '/collections/straw-hats', art: 'straw-flag', children: [
          { label: 'All Straw', href: '/collections/straw-hats', art: 'straw-flag' },
          { label: 'Tahoe', href: '/collections/tahoe-straw', art: 'straw-tahoe' },
          { label: 'Florida', href: '/collections/florida-straw', art: 'straw-florida' },
          { label: 'Idaho', href: '/collections/idaho-straw', art: 'straw-idaho' },
          { label: 'Nevada', href: '/collections/nevada-straw', art: 'straw-nevada' },
          { label: 'Surf', href: '/collections/surf-straw', art: 'straw-surf' },
          /* flag-straw and thb-straw hold the SAME four hats — one tile,
             Adam's name for it: "it will say THB Flag" (2026-08-20) */
          { label: 'THB Flag', href: '/collections/flag-straw', art: 'straw-flag' }
        ] },
        { label: 'Boonies', href: '/collections/boonies', art: 'boonie-gray' }
      ].forEach(function (x) {
        var u = x.label.toUpperCase();
        if (!seen[u]) { seen[u] = 1; items.push(x); }
      });
      return orderMenu(items);
    }
    /* THE GLASS MENU'S OWN ORDER (Adam, 2026-08-17: "change the order of
       the glass menu without touching the order of the menu on desktop")
       — the theme nav is never modified; only this wall sorts by the
       list below. Shopping first, story second, business doors last.
       Anything not listed keeps its natural position after the list. */
    var GLASS_MENU_ORDER = [
      /* picture tiles first, ending at GIFT CARD (Adam, 2026-08-17:
         "$7k club next to color vibes and sale next to 7k club ...
         that will end anything with pictures") */
      'ALL HATS', 'USA', 'FREEDOM', 'SPORTS', 'STATES', 'PASSIONS',
      'COLOR VIBES', '$7K CLUB',
      /* Adam, 2026-08-20: beanies/straw/boonies followed SALE!; Adam,
         2026-09-10: "sale square ... after stickers" everywhere, so SALE!
         now sits between STICKERS and the Instagram tile */
      'BEANIES', 'STRAW', 'BOONIES',
      'HAT ROPES', 'PICKLEBALL PADDLES', 'STICKERS', 'SALE!',
      /* the Instagram glyph tile closes the picture rows (Adam, 2026-08-27) */
      'INSTAGRAM',
      /* then the word-only rows: 3, then 2 centered, then 3 (Adam,
         2026-08-27) — the centering itself is CSS on the CONTACT chip */
      'GIFT CARD', 'OUR STORY', 'BECOME AN AMBASSADOR',
      'CONTACT', 'RETURNS AND EXCHANGES',
      'WHOLESALE', 'CUSTOM HATS', 'BLANK HATS'
    ];
    /* claude- (2026-09-03, Adam: "mobile menu only ... 4 across instead of
       3, that leaves one spot to get a picture ... make that Custom Hats,
       then Wholesale / Blank Hats / Our Story in a set of 3, then in 2's
       Contact and Returns and Exchanges full half width, then Become an
       Ambassador and Gift Card last, half width"). The PHONE wall has its
       own order; the desktop wall keeps GLASS_MENU_ORDER untouched. */
    var GLASS_MENU_ORDER_MOBILE = [
      'ALL HATS', 'USA', 'FREEDOM', 'SPORTS', 'STATES', 'PASSIONS',
      'COLOR VIBES', 'BEANIES', 'STRAW', 'BOONIES',
      'HAT ROPES', 'PICKLEBALL PADDLES', 'STICKERS', 'SALE!', 'INSTAGRAM',
      /* Adam, 2026-09-10: SALE! after STICKERS on the phone wall too */
      /* claude- (2026-09-08, Adam): Custom Hats drops out of the picture rows and
         joins the words — three rows of 3, 3, 2 in this exact order */
      'CUSTOM HATS', 'WHOLESALE', 'BLANK HATS',        /* row of three words */
      'OUR STORY', 'BECOME AN AMBASSADOR', 'GIFT CARD',/* row of three words */
      'CONTACT', 'RETURNS AND EXCHANGES'               /* half width, last */
    ];
    var MENU_MOBILE_THIRD = { 'CUSTOM HATS': 1, 'WHOLESALE': 1, 'BLANK HATS': 1, 'OUR STORY': 1, 'BECOME AN AMBASSADOR': 1, 'GIFT CARD': 1 };
    var MENU_MOBILE_HALF = { 'CONTACT': 1, 'RETURNS AND EXCHANGES': 1 };
    function isPhoneWall() {
      return !!(window.matchMedia && window.matchMedia('(max-width: 1023px)').matches);
    }
    /* forced word buttons even though a collection could dress them */
    var MENU_WORD_ONLY = { 'GIFT CARD': 1, 'CUSTOM HATS': 1 };
    function orderMenu(items) {
      /* claude- (2026-08-27, Adam): "7k club can be gone from everywhere
         menus and everything only people that access it will have a link
         to the page so keep the page live" — the collection stays
         published and reachable by direct link; every menu drops it. */
      items = items.filter(function (it) {
        return String(it.label || '').toUpperCase().indexOf('7K') < 0
          && String(it.href || '').indexOf('7k-club') < 0;
      });
      /* glass renames (Adam, 2026-08-27). "Become an Ambassador" is the
         MOBILE menu's word for it — the desktop nav keeps "Ambassadors".
         Our Story / Custom Hats / Blank Hats are renamed at the THEME
         level (claude-nav-label snippet + header-group), so the DOM
         normally hands them over already renamed — the aliases here are
         a backstop for any surface still emitting the old words. FOLLOW
         US becomes the Instagram glyph tile beside STICKERS. */
      items.forEach(function (it) {
        var u = String(it.label || '').toUpperCase();
        if (u === 'AMBASSADORS') it.label = 'Become an Ambassador';
        if (u === 'OUR MISSION') it.label = 'Our Story';
        if (u === 'CUSTOM') it.label = 'Custom Hats';
        if (u === 'BLANKS') it.label = 'Blank Hats';
        if (u === 'FOLLOW US') {
          it.label = 'Instagram';
          it.ig = true;
          it.href = 'https://www.instagram.com/theheartbeatbrand';
        }
      });
      var rank = {};
      (isPhoneWall() ? GLASS_MENU_ORDER_MOBILE : GLASS_MENU_ORDER).forEach(function (l, i) { rank[l] = i + 1; });
      return items.map(function (it, i) {
        return { it: it, r: rank[it.label.toUpperCase()] || (1000 + i) };
      }).sort(function (a, b) { return a.r - b.r; })
        .map(function (x) { return x.it; });
    }
    function leafPick(href) {
      try {
        var path = new URL(href, window.location.origin).pathname;
        if (!glassRoute(path)) window.location.href = href;
      } catch (e) { window.location.href = href; }
    }
    /* which collection fronts a menu tile's picture (Adam, 2026-08-17:
       Alabama led STATES only by alphabet and "it is one of our worst
       selling hats" — All Hats wears a mountains hat, States wears
       Florida). Unlisted groups borrow their first collection child. */
    var MENU_TILE_IMG = {
      /* COLORFUL LEADS (Adam, 2026-08-27: "wherever there is gray or
         black hats as the main photo outside of the actual product need
         to be a colorful 7 panel that doesn't clash with the background
         ... even tan camo is hard"). THREE-hat family composites, not
         singles ("all hats usa250 freedom and sports are all 1 hat not
         the 3") — only the white pom beanie stays a single (no trio
         shot exists). His picks:
         sports = the soft teal camo fishing hat, beanies = the white
         pom, freedom = red/white/blue flat bill with the white back,
         usa = red/white/blue with the navy back; All Hats wears the
         mountains cream/black/charcoal, States the ocean-blue Florida. */
      'ALL HATS': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/mountains-hat-flat-bill-hat-snapback-cream-black-charcoal-main-side.png?width=360',
      'HATS': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/mountains-hat-flat-bill-hat-snapback-cream-black-charcoal-main-side.png?width=360',
      /* STATES keeps its THREE-hat florida cork photo (Adam, 2026-08-27:
         "you got rid of the 3 hats on the mobile menu you shouldn't have
         ... you can keep the flat bill florida cork" — the ocean-blue
         single blended into the glass) */
      'STATES': '/collections/florida-hats',
      'FREEDOM': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/freedom-hats-flat-bill-snapback-hat-dark-red-white-navy-white-main.png?width=360',
      'USA': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/usa-hat-flat-bill-snapback-red-navy-white-navy-main-side.png?width=360',
      'SPORTS': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/golf-hat-flat-bill-snapback-black-white-camo-main-side.png?width=360',
      'BEANIES': 'https://cdn.shopify.com/s/files/1/0802/0049/9515/files/mountains-beanie-pom-white-front.png?width=360'
    };
    /* STICKERS wears Adam's baseball-heartbeat artwork (the same theme
       asset the hero swatch uses), passed in by the section (Adam,
       2026-08-17). A non-/collections/ value is a direct image URL. */
    /* claude- (2026-09-03): art attributes are read off THIS root, then any
       element on the page, then derived from this script's own asset URL —
       Adam's phone served a cached page (old markup) with the new script,
       so the attribute was missing and Custom Hats fell back to a word
       tile and Stickers to a product photo. The assets folder is always
       the one this script came from, so the fallback cannot go stale. */
    function artAttr(name, assetFile) {
      var v = root.getAttribute(name);
      if (!v) { var any = document.querySelector('[' + name + ']'); v = any ? any.getAttribute(name) : null; }
      if (!v && assetFile) {
        var me = Array.prototype.slice.call(document.scripts).map(function (sc) { return sc.src || ''; })
          .filter(function (u) { return /claude-3d\.js/.test(u); })[0];
        if (me) v = me.replace(/claude-3d\.js.*$/, assetFile);
      }
      return v || null;
    }
    var stickerArt = artAttr('data-sticker-art', 'claude-sticker-baseball.png');
    if (stickerArt) MENU_TILE_IMG['STICKERS'] = stickerArt;
    /* CUSTOM HATS wears the Tahoe Brewfest hat on the PHONE wall only
       (Adam, 2026-09-03) — theme asset claude-menu-custom.png, passed in
       like the sticker art; the desktop wall keeps it as a word button */
    var customArt = artAttr('data-custom-art', 'claude-menu-custom.png');
    /* cut-out beanie art (slot -> asset url), passed the same way the
       sticker art is — see the sections' data-beanie-art attribute */
    var beanieArt = {};
    try { beanieArt = JSON.parse(root.getAttribute('data-beanie-art') || '{}'); } catch (e) {}
    /* which titles belong to a style tile's product wall */
    var STYLE_FILTERS = {
      all: function () { return true; },
      pom: function (t) { return /pom/i.test(t); },
      nocuff: function (t) { return /no cuff/i.test(t); },
      cuffed: function (t) { return /cuff/i.test(t) && !/no cuff/i.test(t); }
    };
    function renderMenu(items, title) {
      paintGrid(function (g) {
        items.forEach(function (it, i) {
          if (/^hats$/i.test(it.label)) it.label = 'All Hats';   /* groups too */
          if (it.ig) {
            /* FOLLOW US is one big Instagram glyph now (Adam, 2026-08-27:
               "a big instagram icon ... next to stickers") — icon only,
               opens the profile in a new tab like the underbill card */
            var igEl = chip(g, i, {
              label: 'Instagram', sub: '',
              onPick: function () { window.open(it.href, '_blank', 'noopener'); }
            });
            igEl.classList.add('c3d-ovl__chip--ig');
            if (isPhoneWall()) igEl.classList.add('c3d-ovl__chip--q');   /* rides the 4-across picture rows (2026-09-03) */
            igEl.setAttribute('aria-label', 'Follow us on Instagram');
            var glyph = document.createElement('span');
            glyph.className = 'c3d-ovl__ig';
            glyph.innerHTML = '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false"><circle cx="15.238" cy="4.948" r="1.238"/><path d="M19.47 3.444A5.11 5.11 0 0 0 16.548.527a7.351 7.351 0 0 0-2.43-.466C13.05.014 12.713 0 9.999 0c-2.712 0-3.057 0-4.12.06A7.351 7.351 0 0 0 3.45.528 5.11 5.11 0 0 0 .528 3.444 7.317 7.317 0 0 0 .06 5.87C.014 6.936 0 7.274 0 9.982s0 3.053.06 4.113c.018.829.176 1.649.468 2.425a5.11 5.11 0 0 0 2.922 2.917 7.35 7.35 0 0 0 2.429.5c1.069.047 1.407.06 4.12.06s3.058 0 4.12-.06a7.351 7.351 0 0 0 2.429-.466 5.11 5.11 0 0 0 2.922-2.918 7.31 7.31 0 0 0 .467-2.424c.047-1.067.06-1.405.06-4.113s0-3.053-.06-4.113a7.317 7.317 0 0 0-.467-2.459zm-1.437 10.537a5.439 5.439 0 0 1-.34 1.843 3.262 3.262 0 0 1-1.87 1.87 5.451 5.451 0 0 1-1.825.34c-1.04.046-1.332.06-3.996.06-2.664 0-2.937 0-3.995-.06a5.451 5.451 0 0 1-1.825-.34 3.255 3.255 0 0 1-1.878-1.87 5.439 5.439 0 0 1-.34-1.823c-.046-1.038-.06-1.33-.06-3.992s0-2.934.06-3.992c.006-.63.121-1.253.34-1.844a3.255 3.255 0 0 1 1.878-1.87 5.451 5.451 0 0 1 1.825-.339c1.038-.046 1.331-.06 3.995-.06s2.937 0 3.996.06c.623.008 1.24.123 1.824.34.86.331 1.54 1.01 1.872 1.87.216.583.331 1.2.34 1.823.046 1.038.06 1.33.06 3.992 0 2.661 0 2.948-.047 3.992h-.014z"/><path d="M9.991 14.753a4.761 4.761 0 1 1 0-9.523 4.761 4.761 0 0 1 0 9.523zm0-1.905a2.857 2.857 0 1 0 0-5.713 2.857 2.857 0 0 0 0 5.713z"/></svg>';
            igEl.insertBefore(glyph, igEl.firstChild);
            return;
          }
          var override = MENU_TILE_IMG[it.label.toUpperCase()];
          /* a non-/collections/ override is a direct image URL (STICKERS
             wears Adam's baseball-heartbeat art) */
          var directImg = (override && !/^\/collections\//.test(override)) ? override : null;
          var imgHref = (!directImg && override) || it.href;
          var colKids = (it.children || []).filter(function (k) {
            return /\/collections\//.test(k.href || '');
          });
          if (!override && colKids.length) imgHref = colKids[0].href;
          /* a tile is a PICTURE tile only when a collection can dress it;
             everything else is a small word button (Adam, 2026-08-17) */
          var hasPic = !MENU_WORD_ONLY[it.label.toUpperCase()]
            && !!(directImg || (imgHref && /\/collections\//.test(imgHref)));
          /* cut-out art beats every other image source for this tile */
          var artImg = (it.art && beanieArt[it.art]) || null;
          if (artImg) { directImg = artImg; hasPic = true; }
          var phone = isPhoneWall();
          var labelU = String(it.label || '').toUpperCase();
          /* 2026-09-08 (Adam): Custom Hats is a word tile on the phone wall now — no Brewfest picture */
          var customPic = false;
          if (customPic) { directImg = customArt; hasPic = true; }
          /* a group whose children are THEMSELVES groups (or style walls)
             re-enters this menu renderer — the collection wall is flat and
             would drop the third level (beanies places, 2026-08-20) */
          var hasSubGroups = (it.children || []).some(function (k) {
            return (k.children && k.children.length) || k.styleWall;
          });
          var el = chip(g, i, {
            label: it.label,
            img: directImg || null,
            /* claude- (2026-08-27): no "N inside" count — Adam: "i don't
               like where it say how many are inside i think thats weird" */
            sub: '',
            onPick: /^all hats$/i.test(it.label)
              /* ALL HATS goes to the real Shop All Hats A-Z PAGE
                 (Adam, 2026-08-28 — the glass directory is retired) */
              ? function () { window.location.href = '/pages/all-hats-a-z?view=claude-all-hats'; }
              : (it.children
                /* a group with collections drills into the SAME wall the
                   takeover uses; a group of plain links (Ambassadors)
                   gets normal-size buttons, not screen-filling boxes */
                ? (hasSubGroups
                  ? function () { go(function () { renderMenu(it.children, it.label); }); }
                  : (colKids.length
                    ? function () { go(function () { renderCollections(it.children, it.label); }); }
                    : function () { go(function () { renderMenu(it.children, it.label); }); }))
                : function () { leafPick(it.href); })
          });
          if (hasPic && !directImg) lazyChipImg(el, imgHref);
          if (!hasPic) el.classList.add('c3d-ovl__chip--txt');
          if (phone) {
            /* the phone rows (2026-09-03): pictures four across, then the
               three-word row, then two half-width pairs — width classes */
            if (hasPic) el.classList.add('c3d-ovl__chip--q');
            if (customPic) el.classList.add('c3d-ovl__chip--custom');   /* the Brewfest hat runs bigger (Adam, 2026-09-03) */
            else if (MENU_MOBILE_HALF[labelU]) el.classList.add('c3d-ovl__chip--half');
            else if (MENU_MOBILE_THIRD[labelU]) el.classList.add('c3d-ovl__chip--third');
          } else if (!hasPic && /^contact$/i.test(it.label)) {
            /* desktop keeps CONTACT leading the centered pair (2026-08-27) */
            el.classList.add('c3d-ovl__chip--pairlead');
          }
        });
        /* claude- (2026-09-08, Adam: "pickleball paddles stickers and instagram
           centered don't change their sizing just center them") — when the
           phone wall's last PICTURE row is short, its first tile gets a lead-in
           margin so the row sits centered; widths untouched. Rendered chips
           are read back so the count is whatever the menu actually has. */
        if (isPhoneWall()) {
          var qs = Array.prototype.slice.call(g.querySelectorAll('.c3d-ovl__chip--q'));
          var rem = qs.length % 4;
          if (rem === 3) qs[qs.length - 3].classList.add('c3d-ovl__chip--triolead');
          else if (rem === 2) qs[qs.length - 2].classList.add('c3d-ovl__chip--duolead');
          else if (rem === 1) qs[qs.length - 1].classList.add('c3d-ovl__chip--sololead');
        }
      }, 'menu', title || 'Menu');
    }
    /* claude- (2026-08-25): the glass menu + router now boot on EVERY page
       (claude-glass-host snippet) — pages with a real c3d section would bind
       these document listeners twice without this guard */
    if (!window.__c3dDocBound) {
    window.__c3dDocBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('button, summary') : null;
      if (!t || (t.closest && t.closest('.c3d-ovl'))) return;
      var sig = ((t.getAttribute && (t.getAttribute('aria-label') || '')) + ' '
        + (t.className || '') + ' ' + (t.id || '')).toLowerCase();
      if (!/(hamburger|burger|menu[-_ ]*(toggle|icon|open|drawer|btn|button)|open[-_ ]*menu)/.test(sig)) return;
      e.preventDefault();
      e.stopPropagation();
      var items = readFullMenu();
      if (items.length) { ovStack = []; go(function () { renderMenu(items); }); }
    }, true);

    /* WHOLESALE INTRO: SOUND ON (Adam, 2026-08-27: "sound on
       automatically on the video when you click on wholesale") — the
       theme autoplays it muted (video_tag mutes any autoplay). This
       tries the unmuted start browsers allow once the visitor has
       clicked within the site (Chrome counts the nav tap that got them
       here); where policy refuses — iOS Safari, cold landings — it
       falls back to muted autoplay and unmutes on the first tap or
       click anywhere on the page. */
    if (window.location.pathname.indexOf('/pages/wholesale') === 0) {
      (function () {
        var tries = 0;
        function arm(v) {
          function un() {
            v.muted = false; v.volume = 1;
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
            document.removeEventListener('pointerdown', un, true);
            document.removeEventListener('touchstart', un, true);
          }
          document.addEventListener('pointerdown', un, true);
          document.addEventListener('touchstart', un, true);
        }
        function attempt() {
          var v = document.querySelector('.cc-video video');
          if (!v) {
            if (++tries < 40) setTimeout(attempt, 300);
            return;
          }
          v.muted = false; v.volume = 1;
          var p = v.play();
          if (p && p.catch) {
            p.catch(function () {
              v.muted = true;
              var p2 = v.play();
              if (p2 && p2.catch) p2.catch(function () {});
              arm(v);
            });
          }
        }
        attempt();
      })();
    }

    /* THE SEARCH HOOKUP (Adam, 2026-08-27): the theme's own search bar
       drives the glass — it keeps focus and value the whole time, the
       glass results wall opens BELOW it, and nothing about the bar or
       the /search page changes server-side. Focusing opens the wall,
       typing re-queries it, Enter queries immediately instead of
       navigating to the old results page. */
    function isSearchBar(el) {
      return !!(el && el.matches
        && !el.closest('.c3d-ovl')
        && el.matches('input[type="search"], input[name="q"]'));
    }
    document.addEventListener('focusin', function (e) {
      if (!isSearchBar(e.target)) return;
      glassSearchOpen(e.target);
      if ((e.target.value || '').trim()) {
        clearTimeout(searchT);
        searchT = setTimeout(function () { runGlassSearch(e.target.value); }, 200);
      }
    }, true);
    document.addEventListener('input', function (e) {
      if (!isSearchBar(e.target)) return;
      glassSearchOpen(e.target);
      clearTimeout(searchT);
      searchT = setTimeout(function () { runGlassSearch(e.target.value); }, 350);
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || !isSearchBar(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      glassSearchOpen(e.target);
      clearTimeout(searchT);
      runGlassSearch(e.target.value);
      try { e.target.blur(); } catch (err) {}   /* fold the phone keyboard */
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || !f.getAttribute || f.closest('.c3d-ovl')) return;
      if (String(f.getAttribute('action') || '').indexOf('/search') < 0) return;
      var qi = f.querySelector('input[name="q"]');
      if (!qi) return;
      e.preventDefault();
      e.stopPropagation();
      glassSearchOpen(qi);
      clearTimeout(searchT);
      runGlassSearch(qi.value);
    }, true);

    /* the click router: every same-origin link on the page flows into the
       glass; links inside a glass page keep the back stack */
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var inGlassBody = a.closest('.c3d-ovl__pagebody');
      if (a.closest('.c3d-ovl') && !inGlassBody) return;   /* glass runs itself */
      if (a.closest('.c3d-flip')) return;   /* the carousel cards bind their
        own glass openers — routing their href here sent PASSIONS to beach
        (its link target) instead of the Passions wall (2026-08-17) */
      if (a.closest('[data-c3d-native]')) return;   /* the collection page's
        A-Z carousel browses PAGE to PAGE on purpose — each collection page
        is now itself the experience (Adam, 2026-08-17) */
      if (a.closest('.fixed-nav-top')) return;   /* the scrolling TOP BAR is
        REAL LINKS again — its pages and collections navigate natively
        (Adam, 2026-08-20: "I need the scrolling top bar to be links again") */
      var raw = a.getAttribute('href') || '';
      if (!raw || raw.charAt(0) === '#') return;
      var url;
      try { url = new URL(a.href, window.location.origin); } catch (err) { return; }
      if (url.origin !== window.location.origin
        && url.hostname.indexOf('theheartbeatbrand.com') < 0) return;
      if (!inGlassBody) ovStack = [];
      if (glassRoute(url.pathname)) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    }

    flips.forEach(function (flip) {
      var drawer = flip.querySelector('.c3d-flip__drawer');
      var cta = flip.querySelector('.c3d-flip__cta');
      if (!cta) return;
      var links = drawer ? Array.prototype.slice.call(drawer.querySelectorAll('a'))
        .map(function (a) {
          return { href: a.getAttribute('href'), label: a.textContent, img: a.getAttribute('data-img') };
        }) : [];
      var m = /\/collections\/([^\/?#]+)/.exec(cta.getAttribute('href') || '');
      var subHandle = m && (SUBSECTIONS[m[1]] || DIRECT_TILES[m[1]]) ? m[1] : null;
      var openFn = null;
      if (links.length) {
        openFn = function (e) {
          e.preventDefault();
          ovStack = [];
          go(function () { renderCollections(links, cta.textContent.trim()); });
        };
      }
      /* claude- (2026-08-24, Adam): the in-glass subsection wall is retired —
         a card with no drawer list is a plain link straight to its collection
         page ("i click on usa ... take me directly into the usa collection").
         Cards WITH a drawer list still open the glass LIST (Sports), and each
         entry in that list navigates. */
      if (openFn) {
        cta.addEventListener('click', openFn);
        var pic = flip.querySelector('.c3d-flip__pic');
        if (pic) pic.addEventListener('click', openFn);   /* the photo too */
      }
      /* cards with neither stay plain links */
    });

    /* (the positional button tint lived here for an hour on 2026-08-17
       and was replaced by fixed zone colors in CSS — Adam: white on the
       main grid, black on the More carousel.) */

    if (reduceMotion) return;   /* the scroll-snap row stands, static */

    loadLibs(false).then(function (libs) {
      var gsap = libs.gsap;
      /* (the idle float was removed 2026-08-17 — Adam: "not floating but
         static". The cards sit still in their 2-across grid.) */

      /* (a 360 wrapping ring lived here for one hour on 2026-08-17 and was
         removed at Adam's call the same night: "don't make it 360 either
         just normal carousel but floating is cool". The scroll-snap row IS
         the carousel; the idle float above is the motion.) */

      /* header rises in */
      var head = root.querySelector('.c3d-ub__head');
      if (head) {
        gsap.from(head.children, {
          y: 16, autoAlpha: 0, duration: .5, stagger: .08, ease: 'power2.out',
          scrollTrigger: { trigger: head, start: 'top 85%' }
        });
      }
    }).catch(function () {});
  }

  /* EVERY scrolling strip melts at an edge when more is hiding past it,
     and only then — never a harsh cut, never a fade over a first item
     with nothing behind it (Adam, 2026-08-17: "that should be
     standard"). Scroll + resize keep the state honest. */
  function edgeFade(el) {
    if (!el || el._c3dFade) return;
    el._c3dFade = true;
    el.classList.add('c3d-fade');
    function upd() {
      var more = el.scrollWidth - el.clientWidth;
      el.classList.toggle('has-left', el.scrollLeft > 2);
      el.classList.toggle('has-right', more > 2 && el.scrollLeft < more - 2);
    }
    el.addEventListener('scroll', upd, { passive: true });
    window.addEventListener('resize', upd);
    setTimeout(upd, 60);
    setTimeout(upd, 700);   /* again once lazy images settle */
  }

  /* ----------------------------------------------------------------- WIRING */
  function initAll(scope) {
    (scope || document).querySelectorAll('[data-c3d-hero]').forEach(initHero);
    (scope || document).querySelectorAll('[data-c3d-underbill]').forEach(initUnderbill);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* theme editor: sections are torn down and re-inserted */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-c3d-hero]');
    if (el && el._c3dDestroy) el._c3dDestroy();
  });
})();

/* claude- (2026-08-28, Adam: "the carousel on the previous page turns
   white in the transition to the new page", after preserveDrawingBuffer
   made it worse): the instant a click commits to a REAL navigation, every
   3D stage canvas goes invisible — the browser's held frame then shows
   the dark gradient instead of an uncaptured white canvas. pageshow
   restores it for back/forward-cache returns. */
(function () {
  function blankStages() {
    Array.prototype.forEach.call(document.querySelectorAll('.c3d-stage canvas'), function (c) {
      c.style.visibility = 'hidden';
    });
  }
  function restoreStages() {
    Array.prototype.forEach.call(document.querySelectorAll('.c3d-stage canvas'), function (c) {
      c.style.visibility = '';
    });
  }
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.target === '_blank' || a.hasAttribute('download')) return;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel):/.test(href)) return;
    /* SYNCHRONOUS: once navigation starts, the outgoing page stops
       compositing — a hide issued even one tick later never reaches the
       screen (proven on Adam's machine, 2026-08-28). Blank now, restore
       a tick later if the glass router handled the click in-page. */
    blankStages();
    setTimeout(function () {
      if (e.defaultPrevented) restoreStages();
    }, 0);
  }, true);
  window.addEventListener('pagehide', blankStages);
  window.addEventListener('pageshow', restoreStages);
})();

/* claude- (2026-08-26): the app side tabs (Judge.me reviews + Klaviyo
   Mystery Discount) wear light glass that disappears once the homepage
   scrolls out of the dark gradient onto white. Toggle c3d-appdark on
   <html> when the tabs' viewport position passes the bottom of the last
   dark claude section — the CSS fades them to dark teal glass. Homepage
   only (Adam's scope); other pages keep the light glass. */
(function () {
  if (!document.querySelector('.cc-claude-3d-hero')) return;
  /* Adam, 2026-08-26: "should switch right when the top of mystery
     discount gets past more..." — the More... header is retired
     (2026-08-27, the grid flows as one), so the trigger line is where it
     used to stand: the top of the 8th underbill card (Straw, the first
     of the old More set), falling back to the grid's bottom edge. The
     probe stays the Mystery Discount tab's own top, measured live. */
  var lineAbs = 0, tabTop = 0;
  function measure() {
    var cards = document.querySelectorAll('.c3d-ub__grid .c3d-flip');
    var anchor = cards[7] || null;
    var grid = document.querySelector('.c3d-ub__grid');
    lineAbs = anchor ? anchor.getBoundingClientRect().top + window.scrollY
      : grid ? grid.getBoundingClientRect().bottom + window.scrollY : 0;
    var teaser = document.querySelector('[class*="kl-teaser"]');
    tabTop = teaser && teaser.getBoundingClientRect().height > 0
      ? teaser.getBoundingClientRect().top
      : window.innerHeight * 0.4;
  }
  function update() {
    var over = lineAbs > 0 && (window.scrollY + tabTop) > lineAbs;
    document.documentElement.classList.toggle('c3d-appdark', over);
  }
  window.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
  window.addEventListener('resize', function () { measure(); update(); });
  /* late measure: the 3D scenes settle their heights after first paint,
     and the Klaviyo teaser mounts on its own clock */
  setTimeout(function () { measure(); update(); }, 600);
  setTimeout(function () { measure(); update(); }, 2000);
})();

/* claude- (2026-08-26): remember which glass collections the shopper browses
   so the cart drawer's Suggested Add-Ons pull from "the same collection or
   collections they are shopping in" (Adam). Most recent first, capped at 4. */
(function () {
  var el = document.querySelector('[data-c3d-collection]');
  if (!el) return;
  var h = el.getAttribute('data-c3d-collection');
  if (!h) return;
  try {
    var list = JSON.parse(localStorage.getItem('c3dShopCols') || '[]');
    list = [h].concat(list.filter(function (x) { return x !== h; })).slice(0, 4);
    localStorage.setItem('c3dShopCols', JSON.stringify(list));
  } catch (e) {}
})();

/* ==================================================== THE SWIPE COMPASS
   (Adam, 2026-08-27, from phone testing: "i could not swipe down the page
   if i was on a left to right swipe ... If i am swiping anywhere from
   north east, north, north west down to south east, south or south west
   and anything inbetween ... that should be controlling the page scroll
   and when i am outside of those boundaries i can be controlling the
   scroller that my finger is touching.")

   So: of the compass's eight sectors, only EAST and WEST — within 22.5°
   of horizontal — belong to the thing under the finger. Everything else
   is the page's, ALWAYS, even when the finger starts on a carousel.

   How: every swipe surface is touch-action: pan-y (the browser owns
   vertical natively, so the page can never get stuck), and this one
   document-level gate holds the first 8px of any touch on a surface
   undecided (preventDefault, so the browser can't commit early), then
   reads the angle once:
     within the E/W cone -> keep preventing; drag drives the surface
       (rails get scrollLeft + a momentum fling, since preventDefault
       killed the native one; the 3D stage/thumbs keep their own pointer
       physics — the gate only shields them from the browser)
     anywhere else -> hands the gesture straight back; the browser turns
       it into page scroll and fires pointercancel at the widgets, whose
       release handlers already cope.
   Delegated on document so dynamically-built rails (cart add-ons, the
   glass PDP gallery) are covered without registration. */
(function () {
  'use strict';
  var WIDGETS = '.c3d-stage, .c3d-thumbs, [data-c3d-title]';
  var RAILS = '.c3d-ub__trustedrail, .ccart__addons-row, .c3d-pcard__strip, ' +
              '.c3d-ovl__pdp-track, .cc-claude-bestsellers .slider';
  /* widened from tan 67.5° (2.414) — Adam, 2026-08-27: "swipe left to
     right right to left can be a little more sensitive". Horizontal now
     claims up to ~34° off-axis; steeper still belongs to the page. */
  var HCONE = 1.5;
  var g = null;

  /* eased scroll follower — one per rail element, lives on the node */
  function railTo(el, x) {
    var max = el.scrollWidth - el.clientWidth;
    el.__c3dTarget = Math.max(0, Math.min(max, x));
    if (el.__c3dRaf) return;
    var last = performance.now();
    el.__c3dRaf = requestAnimationFrame(function step(now) {
      var dt = Math.min(0.05, (now - last) / 1000); last = now;
      var t = el.__c3dTarget, cur = el.scrollLeft, d = t - cur;
      /* rest threshold is a full pixel: the browser rounds scrollLeft
         writes, so a fractional target (180.15 vs a stored 181) can never
         be approached closer than ~1px — at 0.5 the loop span forever and
         snap/behavior stayed parked */
      if (Math.abs(d) < 1 && !el.__c3dDrag) {
        el.scrollLeft = t; el.__c3dRaf = null;
        /* snap parked for the whole glide comes back only now (Adam,
           2026-08-28: Featured Favorites "barely swiping" — settle's fixed
           240ms restore landed mid-glide, and with mandatory snap live
           every scrollLeft write re-snaps to the nearest card, yanking the
           fling back to where it started) */
        if (el.__c3dSnap !== undefined) {
          el.style.scrollSnapType = el.__c3dSnap;
          delete el.__c3dSnap;
        }
        if (el.__c3dBehave !== undefined) {
          el.style.scrollBehavior = el.__c3dBehave;
          delete el.__c3dBehave;
        }
        return;
      }
      /* two ease rates, same lerp: while the finger is down the rail
         chases quickly (14) so it feels attached; after release it decays
         at the hat carousel's own rate (6), which is the long floating
         coast Adam calls GLASS GLIDE (2026-08-28: "feels like it is
         gliding through cards"). The step is never allowed below a whole
         pixel: browsers store scrollLeft quantized, so a fractional nudge
         (335 + 0.22 -> still 335) floors away and the loop stalls a pixel
         short of the target forever, holding snap parked (bitten twice,
         2026-08-28). */
      var stepPx = d * Math.min(1, dt * (el.__c3dDrag ? 14 : (el.__c3dRate || 6)));
      if (Math.abs(stepPx) < 1) stepPx = d > 0 ? 1 : -1;
      el.scrollLeft = cur + stepPx;
      el.__c3dRaf = requestAnimationFrame(step);
    });
  }
  window.__c3dRailTo = railTo;   /* the carousel arrows + desktop wheel ride the same follower */

  document.addEventListener('touchstart', function (e) {
    if (e.touches.length !== 1) {
      /* two fingers ON THE HERO belong to its pinch-to-peek hat zoom
         (Adam, 2026-08-27) — hold the browser's page zoom off so the
         stage's pointer events keep flowing. Two fingers anywhere else
         stay native pinch-zoom. */
      var st = e.target && e.target.closest && e.target.closest('.c3d-stage');
      g = (e.touches.length === 2 && st) ? { mode: 3 } : null;
      return;
    }
    var t = e.target;
    if (!t || !t.closest) { g = null; return; }
    var rail = t.closest(RAILS);
    /* NESTED RAILS — this rule flipped twice on 2026-08-28 and the final
       form is: the PHOTO STRIP wins when it has photos to show. Morning
       (pre-dots): "featured favorites lost its swiping capability ...
       needs to be changed to swipe through the carousel" — outer rail
       won. Evening (strips now circular, dotted, trio-led): "i cant flip
       through the photos on featured favorites anymore" — the strip wins
       again. The carousel is still swiped from anywhere that is not a
       photo: names, prices, the gaps between cards. A strip with fewer
       than two photos has nothing to flip and still defers to the row. */
    if (rail && rail.classList.contains('c3d-pcard__strip') &&
        rail.querySelectorAll('img').length < 2) {
      var outer = rail.parentElement && rail.parentElement.closest(RAILS);
      if (outer) rail = outer;
    }
    var widget = rail ? null : t.closest(WIDGETS);
    if (!rail && !widget) { g = null; return; }
    g = {
      el: rail || widget, isRail: !!rail, mode: 0,
      x: e.touches[0].clientX, y: e.touches[0].clientY,
      sl: rail ? rail.scrollLeft : 0,
      lx: e.touches[0].clientX, lt: e.timeStamp, v: 0,
      snap: null, behave: null
    };
    /* CSS scroll-snap fights a hand-driven drag: we write scrollLeft on every
       touchmove, and `mandatory` snap re-resolves after each write, so the rail
       stutters between snap points instead of tracking the finger (Adam,
       2026-08-28: add-ons "working now but super sensitive to the swipe its
       choppy"). Snap is parked for the drag and restored on release, where the
       settle lands it cleanly. */
    if (rail) {
      rail.__c3dDrag = 1;
      var cs = getComputedStyle(rail);
      var sn = cs.scrollSnapType;
      if (sn && sn !== 'none') {
        g.snap = rail.style.scrollSnapType || '';
        rail.style.scrollSnapType = 'none';
      }
      /* scroll-behavior: smooth (the theme's .slider has it, for its arrow
         buttons) turns every per-frame scrollLeft write into its own
         animated scroll that the next frame overwrites before it moves —
         the drag and the fling both crawl (Adam, 2026-08-28: Featured
         Favorites "barely swiping"). Parked like snap, restored with it. */
      if (cs.scrollBehavior === 'smooth') {
        g.behave = rail.style.scrollBehavior || '';
        rail.style.scrollBehavior = 'auto';
      }
    }
  }, { passive: true, capture: true });

  document.addEventListener('touchmove', function (e) {
    if (!g || g.mode === 2) return;
    if (g.mode === 3) {                        /* hero pinch: browser stays out */
      if (e.cancelable) e.preventDefault();
      return;
    }
    var x = e.touches[0].clientX, y = e.touches[0].clientY;
    var dx = x - g.x, dy = y - g.y;
    if (g.mode === 0) {
      /* NEVER hold the browser while undecided (Adam, 2026-08-27: "i am
         now getting frustrated when trying to swipe ... south southeast
         south west" after a zoom or a sideways swipe) — iOS refuses to
         scroll a gesture once ANY of its moves was prevented, so the old
         8px hold made every vertical swipe that started on a rail feel
         dead. Now vertical starts natively and instantly; we claim only
         flat swipes, and only while the move is still cancelable (if the
         browser committed first, we yield instead of fighting it). */
      if (dx * dx + dy * dy < 36) return;   /* 6px — quicker to engage */
      if (Math.abs(dx) > Math.abs(dy) * HCONE && e.cancelable) {
        g.mode = 1;                            /* E/W — the surface's gesture */
      } else {
        g.mode = 2;                            /* everything else — the page's */
        return;
      }
    }
    if (!e.cancelable) { g.mode = 2; return; } /* browser took it mid-flight */
    e.preventDefault();
    if (g.isRail) {
      /* THE CAROUSEL'S FEEL, ON RAILS (Adam, 2026-08-28: the add-ons rail
         "still isn't smooth", and of the hat carousel, "i like the way the
         carasol swipes"). The hats never write their position directly —
         a drag moves a TARGET and the render loop eases toward it
         (pos += (target - pos) * dt * 6), which filters out the ragged
         timing of raw touch events. Rails were assigning scrollLeft on
         every touchmove, so they inherited that raggedness one-for-one.
         Now they chase a target the same way. */
      railTo(g.el, g.sl - dx);
      var dt = e.timeStamp - g.lt;
      if (dt > 0) {
        /* clamped: a degenerate dt (seen with synthetic events) must not
           turn a short drag into a runaway fling */
        g.v = Math.max(-3, Math.min(3, (x - g.lx) / dt));   /* px per ms */
      }
      g.lx = x; g.lt = e.timeStamp;
    }
  }, { passive: false, capture: true });

  function settle() {
    /* hand the rail its snap back — after the settle animations below have
       been armed, so their eases are never fought either. Rails that get
       the generic momentum glide hand their snap to the follower instead
       (restored when it comes to rest) — a fixed 240ms landed mid-glide
       and mandatory snap yanked the fling back (Adam, 2026-08-28:
       Featured Favorites "barely swiping"). */
    if (g && g.isRail) g.el.__c3dDrag = 0;
    /* every rail settles via the railTo follower now (the PDP track
       joined 2026-08-28, the trusted logos the same day) — snap rides
       the follower handoff, never the 240ms timeout, which would land
       mid-glide and yank */
    var generic = g && g.isRail && g.mode === 1;
    if (g && g.isRail && (g.snap !== null || g.behave !== null)) {
      if (generic) {
        if (g.snap !== null) g.el.__c3dSnap = g.snap;
        if (g.behave !== null) g.el.__c3dBehave = g.behave;
      } else {
        var railEl = g.el, restore = g.snap, rebehave = g.behave;
        setTimeout(function () {
          if (restore !== null) railEl.style.scrollSnapType = restore;
          if (rebehave !== null) railEl.style.scrollBehavior = rebehave;
        }, 240);
      }
    }
    if (g && g.mode === 1 && g.isRail) {
      var el = g.el, v = g.v, startSl = g.sl;
      {
        /* (the trusted logos' one-logo-per-flick branch lived here
           2026-08-27 → 2026-08-28, retired at Adam's call: "give the
           trusted logos the glass slide treatment too" — they ride the
           generic glide below now, like every other rail) */
        /* (the PDP photo track had its own page-one-per-flick branch here
           from launch night through 2026-08-28, retired at Adam's call:
           "you have a swipe on the product photo inside of the collections
           page which is awesome ... if that swipe speed can be translated
           to the photo when i am inside the product glass". It now rides
           the same generic rail as the card strips — 1:1 drag, momentum
           fling, glide decay, snap aligns the landing to a photo.) */
        /* momentum as a TARGET, not a per-frame shove: the follower eases
           into it, so the glide decelerates smoothly instead of stepping.
           The Featured Favorites carousel throws harder — its cards are
           ~half the viewport wide, and at the add-ons rail's 220 a normal
           flick died short of the next snap point and rounded back. */
        el.__c3dDrag = 0;
        /* GLASS GLIDE (Adam, 2026-08-28): Featured Favorites floats free —
           its CSS snap is off (claude-3d.css), so the fling decays
           naturally and rests wherever momentum dies, card hanging off
           the edge and all. An earlier pass rounded the target to card
           boundaries; Adam swapped that for this. */
        var isPdp = el.classList.contains('c3d-ovl__pdp-track');
        var mult = el.closest('.cc-claude-bestsellers') ? 300 : (isPdp ? 320 : 220);
        var target = (el.__c3dTarget === undefined ? el.scrollLeft : el.__c3dTarget) - v * mult;
        /* THE GLASS PHOTO STRIP lands ON a photo (Adam, 2026-09-11: "on
           mobile swiping through the hats on the glass is a little slow").
           Each slide is now a full screen wide, so the generic glide often
           died short of halfway and mandatory snap dragged it back, and
           the long decay (6) plus the snap's own animation read as slow.
           The fling target is rounded to a slide boundary, a deliberate
           flick always moves at least one photo in its direction, and the
           follower settles at nearly twice the rate; snap then has nothing
           left to do. Card rows keep the glide untouched. */
        if (isPdp) {
          var w = el.clientWidth || 1;
          var from = Math.round(startSl / w);
          var to = Math.round(target / w);
          if (Math.abs(v) > 0.3 && to === from) to = from + (v < 0 ? 1 : -1);
          target = Math.max(0, Math.min(el.scrollWidth - w, to * w));
          el.__c3dRate = 11;
        }
        railTo(el, target);
      }
    }
    g = null;
  }
  document.addEventListener('touchend', settle, { passive: true, capture: true });
  document.addEventListener('touchcancel', settle, { passive: true, capture: true });
})();

/* ============================================= STRIP LOOP + DOTS
   (Adam, 2026-08-28: "make the swipe on the product photo on the
   collection page circular so they can swipe left or right to see
   images with the image dots below it so people know they can swipe")

   Every multi-photo card strip (.c3d-pcard__strip — collection pgrid and
   Featured Favorites alike) becomes a RING: a clone of the last photo
   sits before the first and a clone of the first sits after the last,
   the strip boots parked on the real first, and when a glide comes to
   rest on a clone it teleports to the clone's real twin — identical
   pixels, so the switch is invisible and the next swipe keeps going the
   same way. Dots under the strip track the REAL photo. Cards with one
   photo get an empty dots row so every card in a row stays the same
   height (the fixed-slots alignment rule). */
(function () {
  'use strict';
  function enhance(strip) {
    if (strip.__c3dLoop) return;
    strip.__c3dLoop = true;
    var imgs = Array.prototype.filter.call(strip.children, function (c) {
      return c.tagName === 'IMG';
    });
    var n = imgs.length;
    /* speed (P-1154): a card below the first screenful arrives with its
       FIRST photo in data-c3d-src too (the page scrolls in BODY, where the
       browser's own lazy loading does not hold anything back). Load it
       when the card is within a screen and a half of view. */
    if (imgs[0] && imgs[0].hasAttribute('data-c3d-src')) {
      var lead = imgs[0];
      var showLead = function () {
        var real = lead.getAttribute('data-c3d-src');
        if (real) { lead.removeAttribute('data-c3d-src'); lead.src = real; }
      };
      if ('IntersectionObserver' in window) {
        var leadIO = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) { leadIO.disconnect(); showLead(); }
        }, { rootMargin: '1200px 0px' });
        leadIO.observe(strip);
      } else showLead();
    }
    var dots = document.createElement('div');
    dots.className = 'c3d-pcard__dots';
    dots.setAttribute('aria-hidden', 'true');
    strip.parentNode.insertBefore(dots, strip.nextSibling);
    if (n < 2) return;                       /* row-height placeholder only */
    var pips = [];
    for (var i = 0; i < n; i++) {
      var d = document.createElement('i');
      dots.appendChild(d); pips.push(d);
    }
    /* long strips don't show a runway of dots (Adam, 2026-08-28: "the
       dots on the bottom can fade out on the left and right so there
       arent too many dots") — beyond 9 photos, dots fade with distance
       from the current one and disappear past 5 away. */
    function paintDots(real) {
      for (var k = 0; k < n; k++) {
        pips[k].className = k === real ? 'is-on' : '';
        if (n > 9) {
          var dd = Math.abs(k - real);
          var o = dd <= 2 ? 1 : dd <= 3 ? 0.55 : dd <= 5 ? 0.25 : 0;
          pips[k].style.opacity = o;
          pips[k].style.display = o === 0 ? 'none' : '';
        }
      }
    }
    paintDots(0);
    /* ring clones — lazy stays lazy, they sit off-view until swiped to */
    var head = imgs[n - 1].cloneNode(true), tail = imgs[0].cloneNode(true);
    head.setAttribute('aria-hidden', 'true'); tail.setAttribute('aria-hidden', 'true');
    strip.insertBefore(head, imgs[0]);
    strip.appendChild(tail);
    /* speed (P-1154, 2026-09-25): photos after the first arrive as
       data-c3d-src (see the pcard liquid). The two a swipe away — the
       second photo and the last (whose clone sits to the left) — load
       when the card comes near the screen; the whole card loads the
       moment it is touched or swiped. Nothing else about the ring changes. */
    function hydrate(img) {
      var real = img.getAttribute('data-c3d-src');
      if (real) { img.removeAttribute('data-c3d-src'); img.src = real; }
    }
    function hydrateMatching(srcs) {
      Array.prototype.forEach.call(strip.querySelectorAll('img[data-c3d-src]'), function (im) {
        if (srcs.indexOf(im.getAttribute('data-c3d-src')) >= 0) hydrate(im);
      });
    }
    function hydrateAll() {
      Array.prototype.forEach.call(strip.querySelectorAll('img[data-c3d-src]'), hydrate);
    }
    /* 2026-09-25 (Adam: "load two of those each at first and as they swipe
       one of the cards ... the third and fourth load on all product cards"):
       a card near the screen loads its first two photos. The first swipe on
       ANY card sends a page-wide signal: from then on every card on or near
       the screen tops up to four, and cards scrolled to later arrive with
       four. The card actually touched loads all of its own photos. Only
       cards near the screen top up, so one swipe never pulls every photo
       on a long collection at once. */
    function srcsOf(list) {
      return list.map(function (im) { return im && im.getAttribute('data-c3d-src'); }).filter(function (v) { return v; });
    }
    var firstTwo = srcsOf([imgs[0], imgs[1]]);
    var firstFour = srcsOf([imgs[0], imgs[1], imgs[2], imgs[3], imgs[n - 1]]);
    var isNear = false;
    if ('IntersectionObserver' in window) {
      var near = new IntersectionObserver(function (entries) {
        isNear = entries.some(function (e) { return e.isIntersecting; });
        if (isNear) hydrateMatching(window.__c3dSwiped ? firstFour : firstTwo);
      }, { rootMargin: '300px 0px' });
      near.observe(strip);
    } else hydrateAll();
    window.addEventListener('c3d:swiped', function () { if (isNear) hydrateMatching(firstFour); });
    function touched() {
      hydrateAll();
      if (!window.__c3dSwiped) { window.__c3dSwiped = true; window.dispatchEvent(new Event('c3d:swiped')); }
    }
    ['pointerdown', 'touchstart', 'focusin'].forEach(function (ev) {
      strip.addEventListener(ev, touched, { once: true, passive: true });
    });
    strip.addEventListener('scroll', function first() {
      if (strip.__c3dParked) { touched(); strip.removeEventListener('scroll', first); }
    }, { passive: true });
    function W() { return strip.clientWidth || 1; }
    function park() { strip.scrollLeft = W(); }
    park();
    setTimeout(function () { strip.__c3dParked = true; }, 400);
    /* re-park once layout truly settles (fonts/images can shift widths) */
    requestAnimationFrame(park); setTimeout(park, 250);
    var settleT = null;
    strip.addEventListener('scroll', function () {
      var w = W(), idx = Math.round(strip.scrollLeft / w);
      var real = ((idx - 1) % n + n) % n;    /* clone-aware -> real photo */
      paintDots(real);
      clearTimeout(settleT);
      settleT = setTimeout(function () {
        /* at rest on a clone -> jump to its twin. Never mid-gesture or
           mid-glide: the follower owns scrollLeft until it rests. */
        if (strip.__c3dDrag || strip.__c3dRaf) return;
        var w2 = W(), i2 = Math.round(strip.scrollLeft / w2);
        if (i2 === 0) strip.scrollLeft = n * w2;
        else if (i2 === n + 1) strip.scrollLeft = w2;
      }, 140);
    }, { passive: true });
  }
  function pass(root) {
    Array.prototype.forEach.call(
      (root || document).querySelectorAll('.c3d-pcard__strip'), enhance);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { pass(); });
  } else pass();
  /* strips built later (glass walls, section rerenders) get picked up too */
  new MutationObserver(function () { pass(); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();

/* ==================================================== TITLE-BUTTON FIT
   (Adam, 2026-08-27, corrected): titles may wrap, but a WORD never
   splits across lines — with word-break disabled, an overflowing
   scrollWidth means some single word is too wide, and the font steps
   down until every word fits whole. Runs on the grid buttons and the
   A-Z labels; re-runs when fonts land and on resize. */
(function () {
  'use strict';
  function fit(el) {
    el.style.fontSize = '';
    var size = parseFloat(getComputedStyle(el).fontSize) || 13;
    var guard = 24;
    while (el.scrollWidth > el.clientWidth + 1 && size > 7 && guard--) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
  }
  function pass() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.c3d-ub__grid .c3d-flip__cta, .c3d-az__label, .c3d-salebanner, .cc-claude-3d-hero .c3d-thumb__label, .cc-claude-collection-3d .c3d-colhero--cat .c3d-thumb__label'), fit);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pass);
  } else {
    pass();
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(pass);
  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(pass, 150);
  });
})();

/* ================================================ CAROUSEL ARROWS + WHEEL
   (Adam, 2026-09-02: "on all carousels left and right add a click arrow
   button that moves one hat in each direction on mobile and desktop",
   "make the arrow buttons the same glass", and "the featured favorites
   scroll ... too fast" on desktop.)

   Homepage only. Featured Favorites and Shop By Style are the theme's
   <carousel-slider>; its own prev/next (desktop-only, a page per click,
   solid buttons) is hidden in claude-3d.css and these circular glass
   arrows — the trusted-logo arrows' recipe — take over on both widths.
   One CARD per click, aligned to a card edge, ridden by the same railTo
   follower the swipe uses so the glide feel is the house one. The
   desktop trackpad/wheel on Featured Favorites is geared down through
   the same follower (native was 1:1). The hero ring binds its own
   arrows inside initHero. */
(function () {
  'use strict';
  var railTo = window.__c3dRailTo;   /* any page that carries these carousels (2026-09-02: "go out to the collections page and anything else") */
  if (!railTo) return;
  var WHEEL_GEAR = 0.6;    /* px of rail per px of wheel — native is 1:1; 0.28 was "way too slow" (Adam, 2026-09-02) */
  /* DESKTOP CLICK-AND-DRAG (Adam, 2026-09-02: "still quick") — the theme's
     CarouselSlider drags at 2x the mouse and writes scrollLeft raw. On
     these rails a capturing listener keeps its handlers out and the drag
     rides railTo at 0.6x, with a tame fling on release. */
  var DRAG_GEAR = 1.0, FLING = 200;   /* 1:1 with the mouse (theme was 2x); 0.6 was "way too slow" (Adam, 2026-09-02) */
  var FINE = window.matchMedia('(pointer: fine)');
  var ICON = {
    l: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 6l-6 6 6 6"/></svg>',
    r: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 6l6 6-6 6"/></svg>'
  };
  function mk(side, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'c3d-arrow c3d-arrow--' + side;
    b.setAttribute('aria-label', label);
    b.innerHTML = ICON[side];
    return b;
  }
  /* snap and smooth-behavior fight a per-frame follower (see the swipe
     compass above) — parked once here; railTo hands them back at rest */
  function park(el) {
    var cs = getComputedStyle(el);
    if (el.__c3dSnap === undefined && cs.scrollSnapType && cs.scrollSnapType !== 'none') {
      el.__c3dSnap = el.style.scrollSnapType || '';
      el.style.scrollSnapType = 'none';
    }
    if (el.__c3dBehave === undefined && cs.scrollBehavior === 'smooth') {
      el.__c3dBehave = el.style.scrollBehavior || '';
      el.style.scrollBehavior = 'auto';
    }
  }
  function base(el) { return el.__c3dRaf ? el.__c3dTarget : el.scrollLeft; }
  function step(el) {
    var it = el.querySelectorAll('.slider__item');
    return it.length > 1 ? (it[1].offsetLeft - it[0].offsetLeft) : 0;
  }
  function arm(cs) {
    if (cs.__c3dArrows) return;
    var rail = cs.querySelector('.slider');
    if (!rail || cs.querySelectorAll('.slider__item').length < 2) return;
    cs.__c3dArrows = true;
    var aL = mk('l', 'Previous'), aR = mk('r', 'Next');
    cs.appendChild(aL); cs.appendChild(aR);
    function go(dir) {
      var s = step(rail);
      if (!s) return;
      var b = base(rail);
      /* the first card sits one gutter in from the scrollport's edge and
         the theme's snap lands every later card FLUSH to that edge, so
         card k rests at k*s - gutter. Index from there, or a snapped rail
         recomputes the card it is already on (bitten 2026-09-02 on Shop
         By Style: 315 of a 335 step, floor + 1 = the same card). */
      var it = rail.querySelector('.slider__item');
      var g = it ? (it.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft) : 0;
      var idx = (b + g) / s;
      /* land ON a card edge: from a free-floating rest the click finishes
         the partial card first, then one whole card per click */
      var k = dir > 0 ? Math.floor(idx + 0.04) + 1 : Math.ceil(idx - 0.04) - 1;
      park(rail);
      railTo(rail, Math.max(0, k * s - g));
    }
    aL.addEventListener('click', function () { go(-1); });
    aR.addEventListener('click', function () { go(1); });
    function ends() {
      /* the right arrow goes when the LAST CARD is fully in view — the
         rail's max scroll runs one trailing gutter past that, which the
         snap never lets it reach (Shop By Style, two cards: the arrow
         stayed up with nothing left to show) */
      var its = rail.querySelectorAll('.slider__item');
      var lastIt = its[its.length - 1];
      var rr = rail.getBoundingClientRect();
      aL.hidden = rail.scrollLeft < 4;
      aR.hidden = lastIt ? (lastIt.getBoundingClientRect().right <= rr.right + 4)
                         : (rail.scrollLeft > rail.scrollWidth - rail.clientWidth - 4);
    }
    rail.addEventListener('scroll', ends, { passive: true });
    window.addEventListener('resize', ends);
    ends(); setTimeout(ends, 400); setTimeout(ends, 1500);
    if (FINE.matches) {
      var md = false, mx0 = 0, mb = 0, mMoved = 0, mlx = 0, mlt = 0, mv = 0;
      cs.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        e.stopPropagation();   /* the theme's 2x drag never starts */
        md = true; mMoved = 0; mx0 = e.clientX; mb = base(rail);
        mlx = e.clientX; mlt = e.timeStamp; mv = 0;
        rail.__c3dDrag = 1;
        rail.classList.add('is-grabbing');
      }, true);
      cs.addEventListener('mousemove', function (e) {
        e.stopPropagation();
        if (!md) return;
        e.preventDefault();
        var dx = e.clientX - mx0;
        mMoved = Math.max(mMoved, Math.abs(dx));
        var dt = e.timeStamp - mlt;
        if (dt > 0) mv = Math.max(-3, Math.min(3, (e.clientX - mlx) / dt));
        mlx = e.clientX; mlt = e.timeStamp;
        park(rail);
        railTo(rail, mb - dx * DRAG_GEAR);
      }, true);
      function mup(e) {
        if (e && e.type !== 'mouseleave') e.stopPropagation();
        if (!md) return;
        md = false; rail.__c3dDrag = 0;
        rail.classList.remove('is-grabbing');
        if (Math.abs(mv) > 0.4) railTo(rail, base(rail) - mv * FLING);
      }
      cs.addEventListener('mouseup', mup, true);
      cs.addEventListener('mouseleave', mup, true);
      /* a drag is not a click on the card it ends over */
      cs.addEventListener('click', function (e) {
        if (mMoved > 8) { e.preventDefault(); e.stopPropagation(); mMoved = 0; }
      }, true);
    }
    /* the slower desktop wheel — Featured Favorites only (Adam named it) */
    if (cs.closest('.cc-claude-bestsellers')) {
      rail.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   /* page keeps vertical */
        e.preventDefault();
        park(rail);
        railTo(rail, base(rail) + e.deltaX * WHEEL_GEAR);
      }, { passive: false });
    }
  }
  function pass() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.cc-claude-bestsellers carousel-slider, .cc-link-list carousel-slider'), arm);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pass);
  else pass();
})();
